import React from "react";
import { useLocation, useParams } from "react-router-dom";
import HeaderWithIcon from "../components/headers/HeaderWithIcon";
import ReducedBreadcrumb from "../components/breadcrumbs/ReducedBreadcrumb";
import { Spinner, Container, Col, Dropdown, DropdownItem, DropdownMenu, DropdownToggle, Row, Table, Button, Card, CardBody } from "reactstrap";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import QuoteBuilder from "../features/quotes/components/quoteBuilder";
import QuoteCommercials from "../features/quotes/components/quoteCommercials";
import QuoteEditor from "../features/quotes/components/quoteEditor";
import QuoteExports from "../features/quotes/components/quoteExports";
import QuoteOverview from "../features/quotes/components/quoteOverview";
import QuoteReview from "../features/quotes/components/quoteReview";
import QuoteRevisions from "../features/quotes/components/quoteRevisions";
import QuoteRevisionNotes from '../features/quotes/components/quoteRevisionNotes';
import { useQuery } from "@tanstack/react-query";
import { fetchQuote } from "../features/quotes/data/quoteService";
import DataLoader from "../components/loader/DataLoader";
import DataLoaderFailed from "../components/loader/DataLoaderFailed";
import { useTheme } from "../hooks/useTheme";
import debounce from 'lodash/debounce';
import { getCalculatedPropertiesForItems, getDefaultInputValues, stripCalculatedPropertiesFromItem, stripCalculatedPropertiesFromItems } from "../features/quotes/functions/quoteFunctions";
import LockedBanner from "../components/userLocks/LockedBanner";
import { useLockState } from "../components/userLocks/LockCheck";
import { fetchModuleData, fetchModulesList } from "../features/modules/data/moduleService";
import { toast } from "react-toastify";
import isEqual from 'lodash/isEqual';
import { useSalesforceSyncContext } from "../contexts/salesforceSyncContext";

import {
    fetchQuoteRevisionsByQuoteId as fetchQuoteRevisionsByQuoteIdFromDB,
    updateQuoteRevision as updateQuoteRevisionInDB
} from "../features/quotes/data/quoteRevisionService";
import { validateAndNormaliseUrl, verifyFolderExists } from '../features/sharepoint/data/sharepointService';

import { useNavigate } from "react-router-dom";

import {
    createQuoteItem as createQuoteItemInDB,
    updateQuoteItem as updateQuoteItemInDB,
    deleteQuoteItem as deleteQuoteItemInDB,
    deleteQuoteItems as deleteQuoteItemsInDB,
    createQuoteItems as createQuoteItemsInDB,
    updateQuoteItems as updateQuoteItemsInDB,
    fetchQuoteItems
} from "../features/quotes/data/quoteItemService";

import {
    createQuoteModule as createQuoteModuleInDB,
    updateQuoteModule as updateQuoteModuleInDB,
    deleteQuoteModule as deleteQuoteModuleInDB,
    fetchQuoteModules,
    decoupleModule
} from "../features/quotes/data/quoteModuleService";

import { formatCurrency } from "../utils/numberUtils";
import QuoteModal from "../features/quotes/components/quoteModal";
import { fetchItemLookup } from "../features/items/data/itemLookupService";
import { executeLogic } from "../features/flowBuilder/data/quoteGenerationService";

import { formatDateTime } from "../utils/dateUtils";
import { fetchUsers } from "../features/users/data/userService";
import { createAudit, fetchAuditByFilter } from "../features/audit/data/auditService";
import DataTablePageQuery from "../components/DataTablePageQuery";
import { findChangesInObject, findChangesInModuleObject } from "../utils/objectUtils";
import ToastBodyWithDetails from "../components/ToastBodyWithDetails";
import { QuoteDataContext } from "../features/quotes/contexts/quoteDataContext";

const ViewQuote = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const hasRedirectedRef = useRef(false);
    const saveRevision = useRef(true)
    const lockState = useLockState();

    const quoteDataContext = React.useContext(QuoteDataContext);

    const { id: opportunityId, quoteId, quoteRevisionId } = useParams();
    console.log('ViewQuote - useParams values:', { opportunityId, quoteId, quoteRevisionId });
    //const initialTab = quoteRevisionId ? "Overview" : "Revisions";
    const [currentActiveMainTab, setCurrentActiveMainTab] = useState(quoteRevisionId ? "Overview" : "Revisions");

    // Details about this quote revision
    const [quoteRevision, setQuoteRevision] = useState();
    const quoteRevisionRef = useRef();
    const quoteDataRef = useRef();
    const lockStateRef = useRef();
    const [quoteRevisionsFilter, setQuoteRevisionsFilter] = useState({ pageNumber: 1, pageSize: 100 });
    const [quoteRevisionsTotalRecords, setQuoteRevisionsTotalRecords] = useState(0);
    const [quoteHistoryTotalRecords, setQuoteHistoryTotalRecords] = useState(0);
    const [quoteHistoryFilter, setQuoteHistoryFilter] = useState({ pageNumber: 1, pageSize: 10 });
    const [quoteHistoryDownloadButtonIsEnabled, setQuoteHistoryDownloadButtonIsEnabled] = useState(true);
    const quoteHistorySearchableFields = ["timestamp", "user", "action", "notes", "payload"]

    // List of items that are on the quote.  This is a flat list (Not Nested).
    // The basket component will display them nested, as necessary, according to the module
    // That each belongs to (if any), and itemlookup type.  Any other processes that reference
    // basket items should do so using the guid property.  This will not change, when a module is
    // recalculated but the id can change.
    const [quoteItems, setQuoteItems] = useState([]);
    const [autogeneratedQuoteItems, setAutogeneratedQuoteItems] = useState([])
    const [itemLookupCache, setItemLookupCache] = useState([]);
    const [basketProcessingState, setBasketProcessingState] = useState("Basket Processing Idle");
    const [showPageFooter, setShowPageFooter] = useState(false);
    const basketProcessingIsRunningRef = useRef(false);
    const hidePageFooterTimerRef = useRef(null);
    const [isLoadingQuoteItems, setIsLoadingQuoteItems] = useState(true);

    const [salesforceFields, setSalesforceFields] = useState({Opportunity: [], iQuote: []});

    const salesforceSync = useSalesforceSyncContext();

    const opportunityState = useMemo(() => {
        return salesforceFields?.Opportunity?.find(x => x.name === "StageName")?.value || "Unknown";
    }, [salesforceFields]);

    const opportunitySharepointFolder = useMemo(() => {        
        return salesforceFields?.Opportunity?.find(x => x.name === "SharePoint_Folder__c")?.value || null;
    }, [salesforceFields]);

    // Used for sending URL of this quote to salesforce
    const location = useLocation();

    // Define Change History columns
    const quoteHistoryColumns = [
        {
            key: "timestamp",
            label: "Timestamp"
        },
        { key: "user", label: "User" },
        { key: "action", label: "Action" },
        { key: "notes", label: "Notes" },
        { key: "payload", label: "Payload" }
    ];

    const quoteItemsWithCalculatedProperties = useMemo(() => {
        return getCalculatedPropertiesForItems(quoteItems, itemLookupCache, quoteDataContext.salesforceCodes);
    }, [quoteItems, itemLookupCache, quoteDataContext.salesforceCodes]);

    const autogeneratedQuoteItemsWithCalculatedProperties = useMemo(() => {
        return getCalculatedPropertiesForItems(autogeneratedQuoteItems, itemLookupCache, quoteDataContext.salesforceCodes);
    }, [autogeneratedQuoteItems, itemLookupCache, quoteDataContext.salesforceCodes]);

    const enabledQuoteItemsWithCalculatedProperties = useMemo(() => {
        return quoteItemsWithCalculatedProperties.filter(item => !item.disabled);
    }, [quoteItemsWithCalculatedProperties]);

    const enabledAutogeneratedQuoteItemsWithCalculatedProperties = useMemo(() => {
        return autogeneratedQuoteItemsWithCalculatedProperties.filter(item => !item.disabled);
    }, [autogeneratedQuoteItemsWithCalculatedProperties]);

    const allQuoteItemsWithCalculatedProperties = useMemo(() => {
        return [
            ...quoteItemsWithCalculatedProperties,
            ...autogeneratedQuoteItemsWithCalculatedProperties,
        ].map(x => ({...x, lineEditorId: x.quoteModuleGuid ? x.quoteModuleGuid + "_" + x.guid : x.guid}));
    }, [quoteItemsWithCalculatedProperties, autogeneratedQuoteItemsWithCalculatedProperties]);

    const allEnabledQuoteItemsWithCalculatedProperties = useMemo(() => {
        return [
            ...enabledQuoteItemsWithCalculatedProperties,
            ...enabledAutogeneratedQuoteItemsWithCalculatedProperties
        ].map(x => ({...x, lineEditorId: x.quoteModuleGuid ? x.quoteModuleGuid + "_" + x.guid : x.guid}));
    }, [enabledQuoteItemsWithCalculatedProperties, enabledAutogeneratedQuoteItemsWithCalculatedProperties]);

    const [activeBasketProcessingModules, setActiveBasketProcessingModules] = useState([]); // Modules that are marked as basket processing modules, and have an active revision
    const activeBasketProcessingModulesRef = useRef([]);
    const basketProcessingAbortToken = useRef(0); // Incremented to cancel in-flight basket processing when the user switches to a different quote revision
    const isBasketProcessingRunning = useRef(false);
    const [isBasketProcessingActive, setIsBasketProcessingActive] = useState(false); // Reactive mirror of isBasketProcessingRunning for UI gating
    const pendingBasketItems = useRef(null); // Flag to indicate a rerun is needed after current execution completes

    // Fetch data using useQuery
    const {
        data: quoteData = [],
        isLoading: isLoadingQuoteData,
        refetch: refetchQuoteData,
        error: quoteDataError,
    } = useQuery({
        queryKey: ["quote", quoteId],
        queryFn: () => fetchQuote(quoteId),
        enabled: !!quoteId,
        staleTime: 0
    });

    const salesforceOpportunityIsClosed = opportunityState.toLowerCase().startsWith("closed") || opportunityState.toLowerCase() === "opportunity dead";

    const opportunityUpdatesAllowed = (quoteData?.isActive ?? false) && (quoteRevision?.isMain ?? false) && !salesforceOpportunityIsClosed && opportunityState.toLowerCase() !== "unknown";

    const salesforceSyncDisabledReason = salesforceOpportunityIsClosed 
        ? "Opportunity is closed" 
        : opportunityState.toLowerCase() === "unknown" ? "Opportunity state is unknown" 
        : !quoteData?.isActive ? "This is not the active quote for this opportunity" 
        : !quoteRevision?.isMain ? "This is not the main quote revision" : "";

    const getQuoteRevisionsByQuoteIdFromDB = async () => {
        const revisions = await fetchQuoteRevisionsByQuoteIdFromDB(quoteId, quoteRevisionsFilter);
        setQuoteRevisionsTotalRecords(revisions.totalRecords);
        return revisions.revisions;
    }

    const {
        data: quoteRevisions = [],
        isLoading: isLoadingQuoteRevisions,
        error: quoteRevisionsError,
        refetch: refetchQuoteRevisions
    } = useQuery({
        queryKey: ["quoteRevisions", quoteId],
        queryFn: getQuoteRevisionsByQuoteIdFromDB,
        enabled: !!quoteId,
        staleTime: 0
    });

    useEffect(() => {
        if (quoteRevisions) {
            refetchQuoteRevisions();
        }
    }, [quoteRevisionsFilter]);

    useEffect(() => {
        console.log("useEffect: salesforceFields update labels");       
        setSalesforceFields(quoteDataContext.salesforceFields)
    }, [quoteDataContext.salesforceFields]);

    const groupedSalesforceFields = useMemo(() => {
        const opportunitySalesforceFieldsByBasketProcessingStage = activeBasketProcessingModules
            .flatMap(x => x?.postProcessingLogic?.flows ?? [])
            .filter(x => x.nodes.some(node => node.data.action === "Send To Salesforce") )
            .flatMap(flow => ({
                id: flow.guid,
                label: flow.name,                
                sortOrder: "2) " + flow.name,
                items: salesforceFields?.Opportunity?.filter(x => 
                    flow.nodes
                        .filter(n => n?.data?.action === "Send To Salesforce")
                        .flatMap(x => x.data.salesforceFields.filter(field => field.objectName === "Opportunity"))
                        .map(x => x.fieldName).includes(x.name)
                ).map(x => ({...x, id: x.name, sortOrder: x.label})) ?? []
                })
            ).filter(group => group.items.length > 0);

        const iQuoteSalesforceFieldsByBasketProcessingStage = activeBasketProcessingModules
            .flatMap(x => x?.postProcessingLogic?.flows ?? [])
            .filter(x => x.nodes.some(node => node.data.action === "Send To Salesforce") )
            .flatMap(flow => ({
                id: flow.guid,
                label: flow.name,
                sortOrder: "2) " + flow.name,
                items: salesforceFields?.iQuote?.filter(x => 
                    flow.nodes
                        .filter(n => n?.data?.action === "Send To Salesforce")
                        .flatMap(x => x.data.salesforceFields.filter(field => field.objectName === "iQuote"))
                        .map(x => x.fieldName).includes(x.name)
                ).map(x => ({...x, id: x.name, sortOrder: x.label})) ?? []
                })
            ).filter(group => group.items.length > 0);

            console.log("salesforceFieldsByBasketProcessingStage", opportunitySalesforceFieldsByBasketProcessingStage, iQuoteSalesforceFieldsByBasketProcessingStage);

        const nonBasketProcessingOpportunityOverviewGroups = [
            {
                id: "overview",
                label: "Overview",
                sortOrder: "1) Overview",
                items: salesforceFields?.Opportunity?.filter(x => [
                        "Total_Contract_Value__c",
                        "Total_Contract_Margin__c",
                        "Total_Amount__c",
                        "iQuote_Link__c"
                    ].includes(x.name)
                ).map(x => ({...x, id: x.name, sortOrder: x.label}))  ?? []
            }
        ]

        const nonBasketProcessingOpportunityOtherGroups = [
            {
                id: "other",
                label: "Other",
                sortOrder: "9) Other",
                items: salesforceFields?.Opportunity?.filter(x => (![
                    ...nonBasketProcessingOpportunityOverviewGroups.flatMap(g => g.items.map(i => i.name)),
                    ...opportunitySalesforceFieldsByBasketProcessingStage.flatMap(g => g.items.map(i => i.name)),
                    ].includes(x.name))
                ).map(x => ({...x, id: x.name, sortOrder: x.label}))  ?? []
            }
        ]

        const nonBasketProcessingiQuoteOtherGroups = [
            {
                id: "all",
                label: "All",
                sortOrder: "9) All",
                items: salesforceFields?.iQuote?.filter(x => (![
                    ...iQuoteSalesforceFieldsByBasketProcessingStage.flatMap(g => g.items.map(i => i.name)),
                    ].includes(x.name))
                ).map(x => ({...x, id: x.name, sortOrder: x.label}))  ?? []
            }
        ]       

        const salesforceFieldGroupings = {
            pushedToOpportunity: opportunitySalesforceFieldsByBasketProcessingStage,
            pushedToiQuote: iQuoteSalesforceFieldsByBasketProcessingStage,
            inOpportunity: [...nonBasketProcessingOpportunityOverviewGroups, ...nonBasketProcessingOpportunityOtherGroups],
            inIquote: [ ...nonBasketProcessingiQuoteOtherGroups ]
        }

        console.log("groupedSalesforceFields", salesforceFieldGroupings);

        return salesforceFieldGroupings;

    }, [salesforceFields, activeBasketProcessingModules]);

    const getModules = async () => {
        var modules = await fetchModulesList()
        modules = modules.filter(x => x.isSystemModule === true && x.disabled === false);

        // Try to sort by sort order, but if not set, then put at the end sorted by id
        modules = modules.sort((a, b) => (a.sortOrder ?? a.id + 999999) - (b.sortOrder ?? b.id + 999999));

        console.log(modules);
        return modules;
    }

    // Fetch users using useQuery
    const {
        data: basketProcessingModules,
        isLoading: isLoadingBasketProcessingModules,
        error: basketProcessingModulesError,
    } = useQuery({
        queryKey: ["basketprocessingmodules"],
        queryFn: getModules,
        staleTime: 60000,
    });

    useEffect(() => {

        const loadModuleRevisionsForBasketProcessingModules = async () => {
            var loadedProcessingModules = [];

            if (basketProcessingModules) {
                // The initial list is just top level modules.  We need to fetch the revisions for each

                for (const module of basketProcessingModules) {
                    let detailedModuleData = await fetchModuleData(module.id);
                    console.log("Detailed module data", detailedModuleData);

                    if (detailedModuleData) {
                        let activeModuleRevision = detailedModuleData.moduleRevisions.find(x => x.disabled === false);

                        if (activeModuleRevision) {
                            let moduleLogic = JSON.parse(activeModuleRevision.moduleLogicJson);

                            loadedProcessingModules.push({
                                moduleId: module.id,
                                name: module.moduleName,
                                moduleRevisionId: activeModuleRevision.id,
                                inputs: moduleLogic.form.inputs,
                                postProcessingLogic: moduleLogic.processing.postProcessingLogic,
                            })
                        }
                    }
                }

                setActiveBasketProcessingModules(loadedProcessingModules);
            }
        }

        loadModuleRevisionsForBasketProcessingModules();
    }, [basketProcessingModules]);

    useEffect(() => {
        activeBasketProcessingModulesRef.current = activeBasketProcessingModules.filter(x => x.name !== "Salesforce"); // salesforce sync is done by the salesforce sync context, not as a basket processing module, so we need to filter it out of this list
    }, [activeBasketProcessingModules]);

    // When the user switches to a different quote revision while basket processing is running,
    // cancel the in-flight execution so stale results from the previous revision are never applied.
    // pendingBasketItems ensures a fresh run starts for the new revision.
    // Note that if the user switches to a locked revision, the basket processing will abort after 
    // the restart, when it checks the lock state, so we don't need to do anything special to handle that case.
    useEffect(() => {
        if (isBasketProcessingRunning.current) {
            console.warn("⛔ Quote revision changed while basket processing was running — cancelling in-flight execution");
            basketProcessingAbortToken.current++;
            pendingBasketItems.current = true;
        }
    }, [quoteRevision?.id]);

    // I need to keep a ref to the latest value of autogeneratedQuoteItems for the debounced function debouncedExecuteBasketProcessingModules()
    // If I were to just use autogeneratedQuoteItems, it would be stale inside the debounced function
    const autogeneratedItemsWithCalculatedPropertiesRef = useRef([]);
    useEffect(() => {
        autogeneratedItemsWithCalculatedPropertiesRef.current = autogeneratedQuoteItemsWithCalculatedProperties;
    }, [autogeneratedQuoteItemsWithCalculatedProperties]);

    // Keep a ref to the latest value of quoteItemsWithCalculatedProperties to prevent stale data in basket processing
    const quoteItemsWithCalculatedPropertiesRef = useRef([]);
    useEffect(() => {
        quoteItemsWithCalculatedPropertiesRef.current = quoteItemsWithCalculatedProperties;
        setIsLoadingQuoteItems(false);
    }, [quoteItemsWithCalculatedProperties]);

    const itemLookupCacheRef = useRef([]);
    useEffect(() => {
        itemLookupCacheRef.current = itemLookupCache;
    }, [itemLookupCache]);

    const opportunityStateRef = useRef("Unknown");
    useEffect(() => {
        opportunityStateRef.current = opportunityState;
    }, [opportunityState]);

    useEffect(() => {
        quoteRevisionRef.current = quoteRevision;
    }, [quoteRevision]);

    const salesforceSyncExecutionById = useMemo(() => {
        return `Quote ${quoteData?.name} Revision ${quoteRevision?.revisionNumber}`;
    }, [quoteData, quoteRevision]);

    const salesforceSyncExecutionByIdRef = useRef(salesforceSyncExecutionById);
    useEffect(() => {
        salesforceSyncExecutionByIdRef.current = salesforceSyncExecutionById;
    }, [salesforceSyncExecutionById]);

    const [showSalesforceSyncFooter, setShowSalesforceSyncFooter] = useState(false);
    useEffect(() => {
        const isRunning = !['Idle', 'Initialising', 'Error', 'Warning', 'Failed'].includes(salesforceSync.salesforceSyncState)
            && salesforceSync.executingBy === salesforceSyncExecutionById;
        const isOwnCompletedRun = salesforceSync.lastRun?.executedBy === salesforceSyncExecutionById;

        if (isRunning) {
            setShowSalesforceSyncFooter(true);
            return;
        }
        if (isOwnCompletedRun) {
            setShowSalesforceSyncFooter(true);
            const id = setTimeout(() => setShowSalesforceSyncFooter(false), 5000);
            return () => clearTimeout(id);
        }
        setShowSalesforceSyncFooter(false);
    }, [salesforceSync.salesforceSyncState, salesforceSync.lastRun, salesforceSync.executingBy, salesforceSyncExecutionById]);

    const hidePageFooterAfterDelay = () => {
        if (hidePageFooterTimerRef.current) clearTimeout(hidePageFooterTimerRef.current);

        hidePageFooterTimerRef.current = setTimeout(() => {
            hidePageFooterTimerRef.current = null;
            if (basketProcessingIsRunningRef.current) return;
            setShowPageFooter(false);
            setBasketProcessingState(prev => prev.includes("complete") ? "Basket Processing Idle" : prev);
        }, 5000);
    }

    useEffect(() => {
        const updateSalesforce = async () => {
            const allQuoteItems = [...quoteItemsWithCalculatedPropertiesRef.current, ...autogeneratedItemsWithCalculatedPropertiesRef.current];
            setShowPageFooter(true);

            try {
                await salesforceSync.syncToSalesforce({
                    quote: quoteData,
                    quoteRevision: quoteRevisionRef.current,
                    quoteItems: allQuoteItems,
                    executedBy: salesforceSyncExecutionById,
                });
            } finally {
                hidePageFooterAfterDelay();
            }
        }

        if (quoteDataRef.current?.id === quoteData?.id) {
            // User edited the the current quote

            if (!quoteDataRef.current?.isActive && quoteData?.isActive && quoteRevisionRef.current?.isMain) {
                // "active" state of the quote has changed from inactive to active, and this revision is the main revision,
                //  so we should update salesforce now to reflect the new active quote on the opportunity
                updateSalesforce();
            }
        }
        quoteDataRef.current = quoteData;   
    }, [quoteData]);

    useEffect(() => {
        lockStateRef.current = lockState;
    }, [lockState]);

    useEffect(() => {
        return () => {
            if (hidePageFooterTimerRef.current) clearTimeout(hidePageFooterTimerRef.current);
        };
    }, []);

    const compareAndGetDifferences = (existing, updated, moduleName) => {
        let differences = []

        const formatField = (value, type) => {
            if (value === null) return "null";
            if (value === undefined) return "undefined";

            if (type === 'integer') {
                return value.toFixed(0);
            } else if (type === 'currency') {
                return `£${formatCurrency(value)}`;
            } else if (type === 'percentage') {
                return value.toFixed(2) + '%';
            }
            return value;
        }

        const checkField = (fieldName, displayName, type) => {
            if (type === "currency" || type === "percentage") {
                if (existing[fieldName]?.toFixed(2) !== updated[fieldName]?.toFixed(2)) {
                    differences.push({ field: displayName, from: formatField(existing[fieldName], type), to: formatField(updated[fieldName], type) });
                }
            } else if (existing[fieldName] !== updated[fieldName]) {
                differences.push({ field: displayName, from: formatField(existing[fieldName], type), to: formatField(updated[fieldName], type) });
            }
        }

        // We want to keep any existing cost/price overrides that the user made, so copy those values across
        checkField("quantity", "Quantity", "integer");

        checkField("upfrontPrice", "Upfront Price", "currency");
        checkField("recurringPrice", "Recurring Price", "currency");
        checkField("upfrontCost", "Upfront Cost", "currency");
        checkField("recurringCost", "Recurring Cost", "currency");

        checkField("recurringIntervalMonths", "Recurring Interval Months", "integer");
        checkField("recurringStartMonth", "Recurring Start Month", "integer");
        checkField("recurringNumPayments", "Recurring Number of Payments", "integer");

        checkField("internalName", "Internal Name");
        checkField("upfrontNameOnQuote", "Upfront Name on Quote");
        checkField("recurringNameOnQuote", "Recurring Name on Quote");
        checkField("nameOnQuote", "Name on Quote");
        checkField("salesforceCode", "Salesforce Code");

        for (const { label, value } of existing.typeCustomAttributes) {
            const newValue = updated.typeCustomAttributes.find(attr => attr.label === label)?.value;
            if (newValue !== value) {
                differences.push({ field: label, from: value, to: newValue });
            }
        }

        return differences;
    }

    const executeBasketProcessingModules = async () => {
        // Use the ref to get the latest basket items
        const items = quoteItemsWithCalculatedPropertiesRef.current.filter(x => !x.disabled);

        // Capture salesforceSyncExecutionById via ref so it's always current, even though
        // executeBasketProcessingModules is closed over by the debounced function from the first render
        const capturedSalesforceSyncExecutionById = salesforceSyncExecutionByIdRef.current;

        // Don't run if quoteRevision isn't loaded yet
        const currentQuoteRevision = quoteRevisionRef.current;
        if (!currentQuoteRevision || !currentQuoteRevision.id) {
            console.warn("Basket processing skipped - quote revision not fully loaded", currentQuoteRevision);
            return;
        }

        setShowPageFooter(true);
        // Capture the current abort token — if it changes during execution, the user switched revision
        const executionToken = basketProcessingAbortToken.current;
        const userSwitchedRevision = () => basketProcessingAbortToken.current !== executionToken;
        const wasCancelled = ({ when }) => {
            if (!userSwitchedRevision()) return false;
            setBasketProcessingState("Aborting basket processing due to quote revision change");
            console.warn(`⛔ Basket processing cancelled ${when} — quote revision changed`);
            hidePageFooterAfterDelay();
            return true;
        };

        const syncToSalesforce = async (allItems) => {
            if (!userSwitchedRevision() ) {
                const syncResult = await salesforceSync.syncToSalesforce({
                    quote: quoteDataRef.current,
                    quoteRevision: currentQuoteRevision,
                    quoteItems: allItems,
                    executedBy: capturedSalesforceSyncExecutionById,
                });
                if (syncResult.state === "Success") {
                    quoteDataContext.refreshSalesforceFields();
                }
            }
        }

        // Don't run if the revision is locked — show a message and hide the footer after a short delay
        if (currentQuoteRevision.isLocked) {
            setBasketProcessingState("Basket processing will not run, as quote revision is finalised");
            console.warn("Basket processing modules will not run if quote revision is finalised");
            try {
                await syncToSalesforce([...quoteItemsWithCalculatedPropertiesRef.current, ...autogeneratedItemsWithCalculatedPropertiesRef.current]);
            } finally {
                hidePageFooterAfterDelay();
            }
            return;
        } else if (lockStateRef.current && lockStateRef.current?.isLocked) {
            setBasketProcessingState("Basket processing will not run, as the quote is locked by someone else.");
            console.warn("Basket processing modules will not run here while the quote is locked by someone else.");
            try {
                await syncToSalesforce([...quoteItemsWithCalculatedPropertiesRef.current, ...autogeneratedItemsWithCalculatedPropertiesRef.current]);
            } finally {
                hidePageFooterAfterDelay();
            }
            return;
        }

        basketProcessingIsRunningRef.current = true;

        // This list is built up as we execute modules, and we pass this to each subsequent module, so they can see
        // the basket up until the point they are executing
        var basketProcessingItems = [];

        try {
            const startTime = performance.now();
            console.log("executeBasketProcessingRules")

            const currentActiveModules = activeBasketProcessingModulesRef.current;

            if (!currentActiveModules || currentActiveModules.length === 0) {
                setBasketProcessingState("No active basket processing modules")
                console.warn("There are no active basket processing modules")
            }

            const activeBasketProcessingModuleIds = currentActiveModules.map(x => x.moduleId);
            const previouslyGeneratedItemsFromInactiveBasketProcessingModules = autogeneratedItemsWithCalculatedPropertiesRef.current.filter(x => activeBasketProcessingModuleIds.includes(x.moduleId) === false)

            if (previouslyGeneratedItemsFromInactiveBasketProcessingModules.length >= 1) {
                // delete basket items that are no longer relevant
                for (const quoteItem of previouslyGeneratedItemsFromInactiveBasketProcessingModules) {
                    if (wasCancelled({ when: "during inactive module cleanup" })) return;
                    await deleteQuoteItem(quoteItem)
                };

                if (previouslyGeneratedItemsFromInactiveBasketProcessingModules.length === 1) {
                    const toastId = toast.info(<ToastBodyWithDetails
                        header={"Removed Items"}
                        intro={`${previouslyGeneratedItemsFromInactiveBasketProcessingModules[0].quantity} x ${previouslyGeneratedItemsFromInactiveBasketProcessingModules[0].internalName} was automatically removed from the basket because the module that generated it is no longer active.`}
                        onModalClose={() => toast.dismiss(toastId)}
                    />, { autoClose: false });
                } else if (previouslyGeneratedItemsFromInactiveBasketProcessingModules.length > 1) {
                    const toastId = toast.info((<ToastBodyWithDetails
                        header={"Removed Items"}
                        intro={`${previouslyGeneratedItemsFromInactiveBasketProcessingModules.length} items were automatically removed from the basket because the module{s) that generated them are no longer active.`}
                        onModalClose={() => toast.dismiss(toastId)}
                    >
                        <Table>
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {previouslyGeneratedItemsFromInactiveBasketProcessingModules.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.internalName}</td>
                                        <td>{item.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ToastBodyWithDetails>
                    ), { autoClose: false });
                }

                console.log("Removing previously generated items from inactive basket processing modules", previouslyGeneratedItemsFromInactiveBasketProcessingModules);
            }

            if (wasCancelled({ when: "after inactive module cleanup" })) return;

            for (const module of currentActiveModules) {
                setBasketProcessingState(`Processing basket (${module.name})`)
                console.log("Executing basket processing module", module.name);

                const defaultInputValuesForModule = getDefaultInputValues(module.inputs)

                const existingInputs = currentQuoteRevision?.quoteCustomisation?.basketProcessingInputValues?.filter(x => x.moduleId === module.id) ?? []

                const missingInputs = defaultInputValuesForModule.filter(defaultInput =>
                    !existingInputs.some(existing => existing.inputGuid === defaultInput.inputGuid)
                );

                if (missingInputs.length > 0 && !userSwitchedRevision()) {
                    console.log("Adding missing inputs for module", module.name, missingInputs);

                    // Set these missing inputs on the quote revision, so they are there next time
                    updateQuoteRevision("quoteCustomisation", prevCustomisation => {
                        const prevInputs = prevCustomisation.basketProcessingInputValues || [];
                        let newInputs = [...prevInputs];

                        for (const missingInput of missingInputs) {
                            const { inputGuid, value: newValue } = missingInput;

                            const inputValueIndex = newInputs.findIndex(x => x.inputGuid === inputGuid);

                            // Need to check if it's still not there, just in case race conditions mean we already added it
                            if (inputValueIndex === -1) {
                                newInputs.push({ inputGuid, value: newValue });
                            }
                        }

                        return {
                            ...prevCustomisation,
                            basketProcessingInputValues: newInputs, // replaced with new reference
                        };
                    });
                }

                if (wasCancelled({ when: `before executing module "${module.name}"` })) return;

                var result = await executeLogic({
                    logic: module.postProcessingLogic,
                    formInputs: module.inputs,
                    userInputs: [...existingInputs, ...missingInputs],
                    moduleRevisionId: module.moduleRevisionId,
                    basketItems: [...items, ...basketProcessingItems],
                    moduleName: module.name
                })

                if (wasCancelled({ when: `after executing module "${module.name}"` })) return;

                const moduleGeneratedItems = result.quoteItems.map(x => ({
                    ...x,
                    quoteGroupDescription: module.name,
                    moduleId: module.moduleId,
                    moduleRevisionId: module.moduleRevisionId,
                    source: "Basket Processing",
                    quoteRevisionId: currentQuoteRevision.id,

                    // The properties below are returned as JSON, so need to be converted to arrays
                    tags: JSON.parse(x.tags),
                    customAttributes: JSON.parse(x.customAttributes),
                    typeCustomAttributes: JSON.parse(x.typeCustomAttributes)
                }))

                // Work out totals, margins, discounts etc for these returned items, and add to the list of items that will be passed to the next module (if any)
                basketProcessingItems.push(...getCalculatedPropertiesForItems(moduleGeneratedItems, itemLookupCacheRef.current, quoteDataContext.salesforceCodes));

                const previouslyGeneratedQuoteItems = autogeneratedItemsWithCalculatedPropertiesRef.current.filter(x => x.moduleId === module.moduleId)

                // Remove items that were previously generated by the module that are not there any more with the current basket
                const itemsToRemove = previouslyGeneratedQuoteItems.filter(existingItem => !moduleGeneratedItems.some(itemToAdd => itemToAdd.guid === existingItem.guid))

                const quoteItemsByGuid = new Map(previouslyGeneratedQuoteItems.map(item => [item.guid, item]));

                const addedItems = []

                const allItemDifferences = []

                // Add/Update basket items generated by module
                for (const quoteItem of moduleGeneratedItems) {
                    if (wasCancelled({ when: `while adding quote items for module "${module.name}"` })) return;
                    const existing = quoteItemsByGuid.get(quoteItem.guid);

                    if (existing) {
                        let updatedQuoteItem = {
                            ...quoteItem,
                            id: existing.id,

                            // If cost/price/nameOnQuote hasn't been overridden then take the new value from the module, otherwise leave what the user changed
                            // We are still changing original costs/price fields (below), so we can warn user if any new pricing, combined with their overrides
                            // has resulted in discounts out of range, or poor profitability.
                            upfrontCost: existing.upfrontCost === existing.originalUpfrontCost ? quoteItem.upfrontCost : existing.upfrontCost,
                            recurringCost: existing.recurringCost === existing.originalRecurringCost ? quoteItem.recurringCost : existing.recurringCost,
                            upfrontPrice: existing.upfrontPrice === existing.originalUpfrontPrice ? quoteItem.upfrontPrice : existing.upfrontPrice,
                            recurringPrice: existing.recurringPrice === existing.originalRecurringPrice ? quoteItem.recurringPrice : existing.recurringPrice,

                            upfrontNameOnQuote: existing.upfrontNameOnQuote === existing.originalUpfrontNameOnQuote || existing.originalUpfrontNameOnQuote === null || existing.originalUpfrontNameOnQuote === "" ? quoteItem.upfrontNameOnQuote : existing.upfrontNameOnQuote,
                            recurringNameOnQuote: existing.recurringNameOnQuote === existing.originalRecurringNameOnQuote || existing.originalRecurringNameOnQuote === null || existing.originalRecurringNameOnQuote === "" ? quoteItem.recurringNameOnQuote : existing.recurringNameOnQuote,

                            // Just in case the item has changed name in the catalogue since this module last ran,
                            // we want to store the latest value
                            originalUpfrontNameOnQuote: quoteItem.upfrontNameOnQuote,
                            originalRecurringNameOnQuote: quoteItem.recurringNameOnQuote,

                            quoteRevisionId: existing.quoteRevisionId,

                            // We want to store any updated costs/prices from the module, so that we can see new percentages for discounts
                            // that the user has made
                            originalUpfrontCost: quoteItem.upfrontCost,
                            originalRecurringCost: quoteItem.recurringCost,
                            originalUpfrontPrice: quoteItem.upfrontPrice,
                            originalRecurringPrice: quoteItem.recurringPrice
                        }

                        // Replace the basket processing item in the list, so we have the latest version for the next module (including any price overrides etc)
                        updatedQuoteItem = getCalculatedPropertiesForItems([updatedQuoteItem], itemLookupCacheRef.current, quoteDataContext.salesforceCodes)[0]
                        basketProcessingItems = basketProcessingItems.map(item => item.guid === updatedQuoteItem.guid ? updatedQuoteItem : item);

                        let itemDifferences = compareAndGetDifferences(existing, updatedQuoteItem, module.name);

                        if (itemDifferences.length > 0) {
                            allItemDifferences.push({
                                item: updatedQuoteItem,
                                differences: itemDifferences
                            });

                            await updateQuoteItem(updatedQuoteItem, true);
                        }

                    } else {
                        await addQuoteItem({ ...quoteItem, quoteRevisionId: currentQuoteRevision.id });
                        addedItems.push(quoteItem);
                    }
                };

                if (!userSwitchedRevision() && allItemDifferences.length > 0) {
                    // Notify user of all item differences
                    const toastId = toast.info((<ToastBodyWithDetails
                        header={`${module.name}`}
                        intro={`${allItemDifferences.length} item(s) automatically changed`}
                        onModalClose={() => toast.dismiss(toastId)}
                    >
                        <Table>
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Field</th>
                                    <th>Old Value</th>
                                    <th>New Value</th>
                                </tr>
                            </thead>
                            <tbody>
                                {allItemDifferences.map(({ item, differences }) =>
                                    differences.map((diff, index) => (
                                        <tr key={`${item.id}-${diff.field}`}>
                                            {index === 0 && (
                                                <td rowSpan={differences.length} style={{ width: '300px' }}>
                                                    <strong>{item.internalName}</strong>
                                                </td>
                                            )}
                                            <td>{diff.field}</td>
                                            <td>{diff.from}</td>
                                            <td>{diff.to}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>

                    </ToastBodyWithDetails>
                    ), { autoClose: false });
                }

                if (!userSwitchedRevision() && addedItems.length === 1) {
                    const toastId = toast.info(<ToastBodyWithDetails
                        header={`${module.name}`}
                        intro={`${addedItems[0].quantity} x ${addedItems[0].internalName} was automatically added to the basket`}
                        onModalClose={() => toast.dismiss(toastId)}
                    />, { autoClose: false });
                } else if (!userSwitchedRevision() && addedItems.length > 1) {
                    const toastId = toast.info((<ToastBodyWithDetails
                        header={`${module.name}`}
                        intro={`${addedItems.length} items were automatically added to the basket`}
                        onModalClose={() => toast.dismiss(toastId)}
                    >
                        <Table>
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {addedItems.map(item => (
                                    <tr key={item.id}>
                                        <td>{item.internalName}</td>
                                        <td>{item.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ToastBodyWithDetails>
                    ), { autoClose: false });
                }

                if (!userSwitchedRevision() && itemsToRemove.length >= 1) {
                    // delete basket items that are no longer relevant
                    for (const quoteItem of itemsToRemove) {
                        await deleteQuoteItem(quoteItem)
                    };

                    if (itemsToRemove.length === 1) {
                        const toastId = toast.info(<ToastBodyWithDetails
                            header={`${module.name}`}
                            intro={`${itemsToRemove[0].quantity} x ${itemsToRemove[0].internalName} was automatically removed from the basket (${module.name})`}
                            onModalClose={() => toast.dismiss(toastId)}
                        />, { autoClose: false });
                    } else if (itemsToRemove.length > 1) {
                        const toastId = toast.info((<ToastBodyWithDetails
                            header={`${module.name}`}
                            intro={`${itemsToRemove.length} items were automatically removed from the basket`}
                            onModalClose={() => toast.dismiss(toastId)}
                        >
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th>Quantity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {itemsToRemove.map(item => (
                                        <tr key={item.id}>
                                            <td>{item.internalName}</td>
                                            <td>{item.quantity}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </ToastBodyWithDetails>
                        ), { autoClose: false });
                    }
                }

                console.log("Result from basket processing module", moduleGeneratedItems);
            };

            const endTime = performance.now();
            const elapsedSeconds = ((endTime - startTime) / 1000).toFixed(2);
            setBasketProcessingState(`Basket processing complete (${elapsedSeconds}s)`)     

            const allItems = [...items, ...basketProcessingItems];
            await syncToSalesforce(allItems);

            hidePageFooterAfterDelay();
        } catch (error) {
            console.error('Error executing basket processing modules:', error);
            setBasketProcessingState("Error executing basket processing modules");
            // Note that we don't hide the page footer here, as the user needs to see the error message and we don't want it to disappear after 5 seconds.
        } finally {
            basketProcessingIsRunningRef.current = false;
        }
    }

    const isBasketProcessingScheduled = useRef(false); // Tracks if a debounced call is pending
    const justFinishedRunning = useRef(false); // Tracks if we just finished to prevent immediate re-trigger
    const lastBasketProcessingInputValues = useRef(null); // Track input values used in last run to detect user changes vs system defaults

    const debouncedExecuteBasketProcessingModules = useMemo(() => {
        return debounce(async () => {
            console.log("🔵 Debounced function called - isRunning:", isBasketProcessingRunning.current, "isScheduled:", isBasketProcessingScheduled.current);
            
            if (isBasketProcessingRunning.current) {
                // process is currently running, mark that we need to rerun after completion
                console.log("⚠️ Already running - marking for rerun");
                pendingBasketItems.current = true;
                // Keep isScheduled = true since items are queued and will be processed
                return;
            }

            console.log("▶️ Starting basket processing execution");
            isBasketProcessingRunning.current = true;
            setIsBasketProcessingActive(true);
            // Don't clear isScheduled here - keep it true until we're completely done
            justFinishedRunning.current = false;
            
            // Store current input values before execution
            lastBasketProcessingInputValues.current = quoteRevisionRef.current?.quoteCustomisation?.basketProcessingInputValues;
            
            try {
                await executeBasketProcessingModules();
            } finally {
                console.log("✅ Basket processing complete - pending:", pendingBasketItems.current);
                isBasketProcessingRunning.current = false;
                justFinishedRunning.current = true; // Mark that we just finished
                
                // Clear the "just finished" flag to allow all state updates to settle
                setTimeout(() => {
                    console.log("⏰ Clearing justFinishedRunning flag");
                    justFinishedRunning.current = false;
                }, 2000); // 2 seconds should be enough for React to process all batched updates

                // If something was queued during the last execution, process it now.
                // Keep isBasketProcessingActive and isBasketProcessingScheduled true until
                // there is genuinely nothing left to process, so the UI gate never drops
                // prematurely between runs (including the 3s debounce window).
                if (pendingBasketItems.current) {
                    pendingBasketItems.current = null;

                    // Use setTimeout to avoid calling directly from finally block
                    // (avoids stack overflows and gives React room to breathe)
                    setTimeout(() => {
                        debouncedExecuteBasketProcessingModules();
                    }, 0);
                } else {
                    // Only clear the flags when there is truly nothing left to process
                    setIsBasketProcessingActive(false);
                    isBasketProcessingScheduled.current = false;
                }
            }
        }, 3000); // 3 seconds debounce time
    }, []); // Empty dependencies - executeBasketProcessingModules is not a dependency since it's defined above and uses latest values via closure

    useEffect(() => {
        console.log("🔄 Basket processing useEffect triggered - items:", quoteItemsWithCalculatedProperties.length, "modules:", activeBasketProcessingModules.length);
        
        const currentInputValues = quoteRevision?.quoteCustomisation?.basketProcessingInputValues;
        
        // If basket processing is currently running, queue for processing after completion
        if (isBasketProcessingRunning.current || basketProcessingIsRunningRef.current) {
            console.log("⏸️ Skipping - basket processing is running");
            pendingBasketItems.current = true;
            return;
        }

        // Don't trigger if scheduled or just finished (prevents double-execution during initial load)
        if (isBasketProcessingScheduled.current || justFinishedRunning.current) {
            console.log("⏸️ Skipping - already scheduled or just finished");
            return;
        }

        // Run basket processing if there are active modules AND either:
        // - There are regular basket items, OR
        // - There are autogenerated items that may need to be removed
        const hasItemsToProcess = quoteItemsWithCalculatedProperties.length > 0 || autogeneratedQuoteItemsWithCalculatedProperties.length > 0;
        
        if (activeBasketProcessingModules.length > 0 && hasItemsToProcess) {
           console.log("📅 Scheduling basket processing - items:", quoteItemsWithCalculatedProperties.length);
           isBasketProcessingScheduled.current = true; // Mark as scheduled before calling debounced function

           setIsBasketProcessingActive(true); // So they can't finalise the quote whilst we still need to process it
           debouncedExecuteBasketProcessingModules();
        } else {
            console.log("⏭️ Skipping - no active basket processing modules or no items");
        }
    }, [quoteItemsWithCalculatedProperties, activeBasketProcessingModules, quoteRevision?.quoteCustomisation?.basketProcessingInputValues]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            debouncedExecuteBasketProcessingModules.cancel();
        };
    }, [debouncedExecuteBasketProcessingModules]);

    const quoteItemsRef = useRef(quoteItems);
    const autogeneratedQuoteItemsRef = useRef(autogeneratedQuoteItems);
    const attributeUpdateTimerRef = useRef({});

    // Clear any pending attribute debounce timers on unmount to prevent
    // stale DB updates or toasts firing after the component is gone.
    useEffect(() => {
        return () => {
            Object.values(attributeUpdateTimerRef.current).forEach(clearTimeout);
        };
    }, []);

    useEffect(() => {
        quoteItemsRef.current = quoteItems;

        console.log("quoteItems changed")
    }, [quoteItems]);



    useEffect(() => {
        autogeneratedQuoteItemsRef.current = autogeneratedQuoteItems;
    }, [autogeneratedQuoteItems]);

    const getUniqueLookupIdsFromItems = (items) => {
        return [...new Set(
            items
                .map(item => item.itemLookupId)
                .filter(id => id != null)
        )];
    }

    const fetchUncachedItemLookups = async (items, currentlyCachedItemLookups) => {
        // 1. Get unique itemLookupIds
        const uniqueLookupIds = getUniqueLookupIdsFromItems(items);
        const lookupIdsNotAlreadyLoaded = uniqueLookupIds.filter(id => !currentlyCachedItemLookups.some(x => x.id === id));

        // 2. Fetch all lookups in parallel (Ideally we would batch this on the backend)
        const lookupsArray = await Promise.all(
            lookupIdsNotAlreadyLoaded.map(id => fetchItemLookup(id))
        );


        console.log("Fetched item lookups for in-use items", lookupsArray);

        return lookupsArray;
    };

    const debouncedFetchLookups = useMemo(() => {
        return debounce(async (items) => {
            if (items.length === 0) {
                setItemLookupCache([]);
                return;
            }
            try {
                const inUseIds = getUniqueLookupIdsFromItems(items);

                console.log("In-use item lookup ids", inUseIds, itemLookupCache);

                if (itemLookupCache.map(x => x.id).sort().join(',') === inUseIds.sort().join(',')) {
                    console.log("Item lookups already loaded for in-use items, skipping fetch");
                    return;
                }

                const itemLookupsToStore = await fetchUncachedItemLookups(items, itemLookupCache);

                // Store it in the item cache, ensuring no duplicates, so we don't need to query the DB again for these items whilst on this quote
                setItemLookupCache(prevCache => {
                    const combined = [...prevCache, ...itemLookupsToStore];
                    const unique = Array.from(new Map(combined.map(i => [i.id, i])).values());
                    return unique;
                });
            } catch (error) {
                console.error('Error fetching item lookups:', error);
                setItemLookupCache([]);
            }
        }, 1000); // 1 second debounce time
    }, [itemLookupCache]);

    useEffect(() => {
        debouncedFetchLookups([...quoteItems, ...autogeneratedQuoteItems]);

        // Cleanup debounce on unmount
        return () => {
            debouncedFetchLookups.cancel();
        };

    }, [autogeneratedQuoteItems.length, quoteItems.length, quoteRevision?.id]);

    // List of modules that are on the quote, along with the inputs used on the modal for each.
    const [quoteModules, setQuoteModules] = useState([]);

    const [totalContractRevenue, setTotalContractRevenue] = useState("£0");
    const [totalContractProfit, setTotalContractProfit] = useState("£0");

    const updateQuoteRevision = (field, valueOrUpdater) => {
        if (valueOrUpdater === undefined) {
            toast.error(`Value not specified for quote revision update on ${(field ?? "unspecified")} field`);
            return;
        }

        setQuoteRevision(prev => {
            const value = typeof valueOrUpdater === "function"
                ? valueOrUpdater(prev[field])
                : valueOrUpdater;

            return {
                ...prev,
                [field]: value
            };
        });
    };

    const handleAddWarning = (warning) => {
        if (!warning || !warning.id || !warning.sectionId ||
            warning.acknowledged === undefined || warning.approved === undefined ||
            warning.acknowledgedBy === undefined || warning.approvedBy === undefined) {
            console.error("Invalid warning object", warning);
            return;
        }

        updateQuoteRevision("quoteOrderInformation", (prevOrderInformation) => {
            const existingWarnings = prevOrderInformation.warnings || [];

            // Try to find if it already exists
            const exists = existingWarnings.some(
                (w) => w.id === warning.id && w.sectionId === warning.sectionId
            );

            let newWarnings;
            if (exists) {
                // Update existing warning (Shouldn't get here really, but just in case,
                // we will protect against duplicates by updating existing)
                newWarnings = existingWarnings.map((w) =>
                    w.id === warning.id && w.sectionId === warning.sectionId
                        ? warning
                        : w
                );
            } else {
                // Add new warning
                newWarnings = [...existingWarnings, warning];
            }

            return {
                ...prevOrderInformation,
                warnings: newWarnings
            };
        });
    };

    const handleRemoveWarning = (warning) => {
        console.log("Removing warning", warning);
        updateQuoteRevision("quoteOrderInformation", prevOrderInformation => {
            const newWarnings = !prevOrderInformation.warnings ? [] : prevOrderInformation.warnings.filter(w => !(w.id === warning.id && w.sectionId === warning.sectionId));
            return {
                ...prevOrderInformation,
                warnings: newWarnings
            };
        });
    };

    const handleAcknowledgedWarningChange = (id, newValue) => {
        updateQuoteRevision("quoteOrderInformation", prevOrderInformation => {
            const newWarnings = !prevOrderInformation.warnings ? [] : prevOrderInformation.warnings.map(warning =>
                warning.id === id ? {
                    ...warning,
                    acknowledged: newValue,
                    acknowledgedBy: newValue ? quoteDataContext.currentUser?.userGuid : null,
                } : warning
            );

            const updated = {
                ...prevOrderInformation,
                warnings: newWarnings
            };

            return updated;
        });
    }

    const handleApprovedWarningChange = (id, newValue) => {
        updateQuoteRevision("quoteOrderInformation", prevOrderInformation => {
            const newWarnings = !prevOrderInformation.warnings ? [] : prevOrderInformation.warnings.map(warning =>
                warning.id === id ? {
                    ...warning,
                    approved: newValue,
                    approvedBy: newValue ? quoteDataContext.currentUser?.userGuid : null,
                } : warning
            );

            const updated = {
                ...prevOrderInformation,
                warnings: newWarnings
            };

            return updated;
        });
    }

    const lockRevision = async () => {
        const lockResult = await updateQuoteRevisionInDB({ ...quoteRevision, isLocked: true }); // save the revision with the updated lock
        if (!lockResult?.ok) {
            toast.error(`Failed to lock quote revision ${quoteRevision?.revisionNumber ?? ""}`.trim());
            console.error("Failed to lock quote revision", lockResult);
            return false;
        }

        console.log("♻️ LockRevision saved to DB");

        saveRevision.current = false // tell quoteRevision useEffect to not save the revision again, just refresh the banner through the refreshRevisionLock method
        updateQuoteRevision("isLocked", true); // the revision is now finalised and locked. So the quoteRevision useEffect will refresh the banner through the refreshRevisionLock method

        setTimeout(() => {
            lockState.notifyLockOverride(); // triggers an event in SignalR that notifies all users who have opened the revision to update their data
            console.log("📨 Notifying other users...");
        }, 800);

        return true;
    }

    const deleteQuoteItem = async (quoteItemToDelete) => {
        const success = await deleteQuoteItemInDB(quoteItemToDelete);

        if (success) {
            if (quoteItemToDelete.source !== "Basket Processing") {
                setQuoteItems(prev => prev.filter(x => !(x.guid === quoteItemToDelete.guid && x.quoteModuleGuid === quoteItemToDelete.quoteModuleGuid)));
                setBasketProcessingState("Basket Has Changed");
                setShowPageFooter(true);
            } else {
                setAutogeneratedQuoteItems(prev => prev.filter(x => x.guid !== quoteItemToDelete.guid));
            }
            const currentQuoteRevision = quoteRevisionRef.current;
            addAudit({ action: "Item deleted from quote", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: { "revision number": currentQuoteRevision.revisionNumber, item: { id: quoteItemToDelete.itemLookupId ?? quoteItemToDelete.id, internalName: quoteItemToDelete.internalName } } });
        } else {
            toast.error(`Failed to delete ${quoteItemToDelete.internalName} from database`);
        }
    }

    const deleteQuoteItems = async (quoteItemsToDelete) => {
        const success = await deleteQuoteItemsInDB(quoteItemsToDelete);

        if (success) {
            const guidsToDelete = new Set(quoteItemsToDelete.map(item => item.lineEditorId ?? item.guid));

            if (quoteItems.some(item => guidsToDelete.has(item.lineEditorId ?? item.guid))) {
                setBasketProcessingState("Basket Has Changed");
                setShowPageFooter(true);
            }

            setQuoteItems(prev => prev.filter(x => !guidsToDelete.has(x.lineEditorId ?? x.guid)));
            setAutogeneratedQuoteItems(prev => prev.filter(x => !guidsToDelete.has(x.lineEditorId ?? x.guid)));
            const currentQuoteRevision = quoteRevisionRef.current;
            const payload = {
                "revision number": currentQuoteRevision.revisionNumber,
                items: quoteItemsToDelete.map(item => ({
                    id: item.itemLookupId ?? item.id,
                    internalName: item.internalName
                }))
            };
            addAudit({
                action: "Items deleted from quote",
                entity: "quote",
                entityId: currentQuoteRevision.quoteId,
                payload
            });
        } else {
            toast.error(`Failed to delete items from database`);
        }
    }

    const updateQuoteItem = async (updatedBasketItem, basketProcessingUpdate = false) => {
        // Find the existing item
        const allItems = updatedBasketItem.source !== "Basket Processing" ? quoteItems : autogeneratedQuoteItems;
        const existingItem = allItems.find(x =>
            updatedBasketItem.quoteModuleGuid
                ? x.quoteModuleGuid === updatedBasketItem.quoteModuleGuid && x.guid === updatedBasketItem.guid
                : x.guid === updatedBasketItem.guid
        );

        // Strip calculated/UI-only fields before comparing — existingItem is raw state,
        // but updatedBasketItem may come from allQuoteItemsWithCalculatedProperties and carry
        // extra derived fields (margins, totals, lineEditorId, etc.) that would always differ.
        const strippedItem = stripCalculatedPropertiesFromItem(updatedBasketItem);

        // 🔁 Skip update if nothing has changed
        if (existingItem && isEqual(existingItem, strippedItem)) {
            console.log("Item not changed, skipping update");
            return;
        }

        const success = await updateQuoteItemInDB(strippedItem)

        if (success) {
            if (updatedBasketItem.source !== "Basket Processing") {
                console.log("updating quote item (User)", updatedBasketItem.internalName);
                setQuoteItems(prev => prev.map(x =>
                    (updatedBasketItem.quoteModuleGuid
                        ? x.quoteModuleGuid === updatedBasketItem.quoteModuleGuid && x.guid === updatedBasketItem.guid
                        : x.guid === updatedBasketItem.guid)
                        ? updatedBasketItem : x))
                setBasketProcessingState("Basket Has Changed");
                setShowPageFooter(true);
            } else {
                console.log("updating quote item (Basket Processing)", updatedBasketItem.internalName);
                setAutogeneratedQuoteItems(prev => prev.map(x => x.guid === updatedBasketItem.guid ? updatedBasketItem : x))

                if (!basketProcessingUpdate && // User made the change, not basket processing

                    // Cost or price changed
                    (existingItem.upfrontPrice !== updatedBasketItem.upfrontPrice ||
                        existingItem.recurringPrice !== updatedBasketItem.recurringPrice ||
                        existingItem.upfrontCost !== updatedBasketItem.upfrontCost ||
                        existingItem.recurringCost !== updatedBasketItem.recurringCost)) {

                    // If user changed cost/price on an autogenerated item, then re-run basket processing modules to ensure everything is up to date
                    // Note that this happens after a delay, so the basketProcesingItems state should have updated by the time it runs.
                    debouncedExecuteBasketProcessingModules();
                }
            }
            auditItemUpdate(updatedBasketItem)
        } else {
            toast.error(`Failed to update ${updatedBasketItem.internalName} in database`);
        }
    }

    const updateQuoteItemTypeAttribute = (id, attribute, newValue) => {

        // Optimistically update state immediately using the functional updater form so that
        // rapid successive calls each apply their change to the latest state without overwriting
        // each other (e.g. two different attributes changed within the same render cycle).
        setQuoteItems(prev => {
            const item = prev.find(x => x.id === id);
            if (!item) return prev;
            return prev.map(x => {
                if (x.id !== id) return x;
                const existing = x.typeCustomAttributes ?? [];
                const attributeExists = existing.some(attr => attr.label === attribute);
                const updatedAttributes = attributeExists
                    ? existing.map(attr => attr.label === attribute ? { ...attr, value: newValue } : attr)
                    : [...existing, { label: attribute, value: newValue }];
                return { ...x, typeCustomAttributes: updatedAttributes };
            });
        });

        // Debounce the DB save per item so that if multiple attributes are changed in quick
        // succession, we wait for all of them to settle in state before saving. When the timer
        // fires, quoteItemsRef.current will have the fully-accumulated latest state.
        const timerKey = String(id);
        if (attributeUpdateTimerRef.current[timerKey]) {
            clearTimeout(attributeUpdateTimerRef.current[timerKey]);
        }
        attributeUpdateTimerRef.current[timerKey] = setTimeout(async () => {
            delete attributeUpdateTimerRef.current[timerKey];

            const latestItem = quoteItemsRef.current.find(x => x.id === id);
            if (!latestItem) {
                toast.error(`Failed to find item in quote items`);
                return;
            }

            const success = await updateQuoteItemInDB(latestItem);
            if (success) {
                auditItemUpdate(latestItem);
            } else {
                toast.error(`Failed to update ${latestItem.internalName} in database`);
            }
        }, 300);
    }

    const updateQuoteItems = async (updatedBasketItems, basketProcessingUpdate = false) => {

        const updatedBasketProcessingItems = updatedBasketItems.filter(item => item.source === "Basket Processing");
        const updatedUserItems = updatedBasketItems.filter(item => item.source !== "Basket Processing");

        const allItems = [...quoteItems, ...autogeneratedQuoteItems];

        // Strip calculated/UI-only fields from all incoming items in one batch, so that
        // differences in derived values (margins, totals, lineEditorId, etc.) don't cause
        // spurious saves when nothing storable has actually changed.
        const strippedBasketItems = stripCalculatedPropertiesFromItems(updatedBasketItems);

        // Determine which items actually changed to avoid unnecessary database updates
        // and state changes
        const changedItemsStripped = strippedBasketItems.filter(strippedItem => {
            const existingItem = allItems.find(x =>
                strippedItem.quoteModuleGuid
                    ? x.quoteModuleGuid === strippedItem.quoteModuleGuid && x.guid === strippedItem.guid
                    : x.guid === strippedItem.guid
            );
            return existingItem && !isEqual(existingItem, strippedItem);
        });

        if (changedItemsStripped.length === 0) {
            console.log("No items were actually changed, skipping update");
            return;
        }

        const success = await updateQuoteItemsInDB(changedItemsStripped)

        // We need to work out calculated properties for this item because when we do the audit of
        // changes we could otherwise find that it says the user changed these values.  But they
        // in fact didn't, they automatically changed as a result of changes the user made to other fields
        // that do affect calculated properties (e.g. quantity, cost, price).  This would tend to happen
        // more if they update the same item multiple times in a row, as you could, for example, change
        // the quantity, see the total cost/price update automatically, then go back in and change the the
        // autotask code.  The editing window might still be working with the old calculated properties,
        // so if we didn't recalculate them here, the audit would show that the user changed the autotask code
        // and also changed the total cost/price, when in fact they only changed the autotask code and the
        // total cost/price changes were automatic as a result of the previous quantity change.
        // Note: getCalculatedPropertiesForItems only reads raw/storable fields, so stripped items are sufficient.
        const changedItemsWithUpdatedStats = getCalculatedPropertiesForItems(changedItemsStripped, itemLookupCacheRef.current, quoteDataContext.salesforceCodes);

        if (success) {
            if (updatedUserItems.length > 0) {
                console.log("updating quote items (User)", updatedUserItems);
                setQuoteItems(prev => prev.map(x => {
                    const updatedBasketItem = changedItemsWithUpdatedStats.find(item =>
                         item.quoteModuleGuid
                            ? item.quoteModuleGuid === x.quoteModuleGuid && item.guid === x.guid
                            : item.guid === x.guid
                    );
                    return updatedBasketItem ?? x;
                }))                    
            } 
            
            if (updatedBasketProcessingItems.length > 0) {
                console.log("updating quote items (Basket Processing)", updatedBasketProcessingItems);

                setAutogeneratedQuoteItems(prev => prev.map(x => {
                    const updatedBasketItem = changedItemsWithUpdatedStats.find(item => item.guid === x.guid);
                    return updatedBasketItem?.guid === x.guid ? updatedBasketItem : x
                }))

                // Now we need to determine if basket processing needs to run again. We want to avoid 
                // running it unnecessarily if the changes were just a result of basket processing
                // itself (to prevent infinite loops) but we do want to run it if the user made a
                // change that affects cost/price, as that could impact other items through 
                // the basket processing rules.

                if (!basketProcessingUpdate) {
                    let costOrPriceChanged = false;

                    for (const updatedBasketItem of updatedBasketProcessingItems) {
                        const existingItem = autogeneratedQuoteItems.find(x => x.guid === updatedBasketItem.guid);

                        // Cost or price changed
                        if (existingItem.upfrontPrice !== updatedBasketItem.upfrontPrice ||
                            existingItem.recurringPrice !== updatedBasketItem.recurringPrice ||
                            existingItem.upfrontCost !== updatedBasketItem.upfrontCost ||
                            existingItem.recurringCost !== updatedBasketItem.recurringCost) {
                            costOrPriceChanged = true;
                            break;
                        }

                    }

                    if (costOrPriceChanged) {

                        // If user changed cost/price on an autogenerated item, then re-run basket processing modules to ensure everything is up to date
                        // Note that this happens after a delay, so the basketProcesingItems state should have updated by the time it runs.
                        debouncedExecuteBasketProcessingModules();
                        setBasketProcessingState("Basket Has Changed");
                        setShowPageFooter(true);
                    }
                }

            }

            auditItemsUpdate(changedItemsWithUpdatedStats)
        } else {
            toast.error(`Failed to update items in database`);
        }
     };      

    const addQuoteItem = async (newBasketItem) => {
        const revisionId = newBasketItem.quoteRevisionId > 0 
            ? newBasketItem.quoteRevisionId 
            : quoteRevisionId ?? quoteRevisionRef.current?.id;
            
        if (!revisionId) {
            toast.error(`Quote revision ID is missing`);
            return;
        }

        var result = await createQuoteItemInDB({ ...newBasketItem, quoteRevisionId: revisionId })

        if (result?.id) {
            if (newBasketItem.source !== "Basket Processing") {
                setQuoteItems(prev => [...prev, result])
                setBasketProcessingState("Basket Has Changed");
                setShowPageFooter(true);
            } else {
                setAutogeneratedQuoteItems(prev => [...prev, result])
            }
            const currentQuoteRevision = quoteRevisionRef.current;
            addAudit({ action: "Item added to quote", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: { "revision number": currentQuoteRevision.revisionNumber, item: { id: result.id, internalName: newBasketItem.internalName, nameOnQuote: newBasketItem.isUpfront && newBasketItem.isRecurring ? `${newBasketItem.upfrontNameOnQuote} / ${newBasketItem.recurringNameOnQuote}` : newBasketItem.isUpfront ? newBasketItem.upfrontNameOnQuote : newBasketItem.recurringNameOnQuote } } })
        } else {
            toast.error(`Failed to add ${newBasketItem.internalName} to database`);
        }
    }

    const addQuoteItems = async (newBasketItems) => {
        var userItems = [];
        var autoItems = [];

        const revisionId = quoteRevisionId ?? quoteRevisionRef.current?.id;
        const items = await createQuoteItemsInDB(newBasketItems.map(x => ({ ...x, quoteRevisionId: revisionId })));

        const addedByBasketProcessing = newBasketItems.length > 0 && newBasketItems[0].source === "Basket Processing";

        if (Array.isArray(items) && items.length > 0) {
            if (!addedByBasketProcessing) {
                userItems = items;
            } else {
                autoItems = items;
            }

            auditItemsAdd(items);

            if (items.length < newBasketItems.length) {
                toast.error(`Only ${items.length} of ${newBasketItems.length} items were added to the database`);
            }

            // Batch update state to trigger basket processing only once
            if (!addedByBasketProcessing) {
                setQuoteItems(prev => [...prev, ...userItems]);
                setBasketProcessingState("Basket Has Changed");
                setShowPageFooter(true);
            } else {
                setAutogeneratedQuoteItems(prev => [...prev, ...autoItems]);
            };
        } else {
            toast.error(`Failed to add items to database`);
        }
     };    

    const deleteQuoteModule = (quoteModuleToDelete) => {
        setQuoteModules(prev => prev.filter(x => x.guid !== quoteModuleToDelete.guid))
        deleteQuoteModuleInDB(quoteModuleToDelete)

        auditModuleDeletion(quoteModuleToDelete)
    }

    const updateQuoteModule = (updatedBasketModule, moduleRevisionFormInputs) => {
        setQuoteModules(prev => prev.map(x => x.guid === updatedBasketModule.guid ? updatedBasketModule : x))
        updateQuoteModuleInDB(updatedBasketModule)

        auditModuleUpdate(updatedBasketModule, moduleRevisionFormInputs)
    }

    const addQuoteModule = async (newBasketModule) => {
        var result = await createQuoteModuleInDB({ ...newBasketModule, quoteRevisionId: quoteRevisionId ?? quoteRevisionRef.current?.id })

        if (result?.id) {
            setQuoteModules(prev => [...prev, result])

            auditModuleInsertion(newBasketModule)
        } else {
            alert("Failed to add to Database")
        }
    }

    const decoupleQuoteModule = async (quoteModuleGuid, groupName) => {
        // Save changes to DB
        var result = await decoupleModule(quoteModuleGuid);
        if (result) {
            // Compute itemIds from the ref (pure read, no side effects) before updating state
            const itemIds = quoteItemsRef.current
                .filter(f => f.quoteModuleGuid === quoteModuleGuid)
                .map(f => f.id);

            // It worked, so update the UI to match
            setQuoteItems(prev => prev.map(f => {
                if (f.quoteModuleGuid !== quoteModuleGuid) return f;
                return { ...f, quoteModuleGuid: null, quoteGroupDescription: "Custom " + groupName };
            }));
            setQuoteModules(prev => prev.filter(f => f.guid !== quoteModuleGuid));

            toast.success("Decoupled module");

            const currentQuoteRevision = quoteRevisionRef.current;
            addAudit({ action: "Decoupled module", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: { "revision number": currentQuoteRevision.revisionNumber, group: groupName, itemIds: itemIds } })
        }
        else {
            toast.error("Failed to decouple");
        }
    }


    const calcTotalContractRevenueAndProfit = () => {
        var totalCost = 0;
        var totalPrice = 0;

        allEnabledQuoteItemsWithCalculatedProperties.forEach(quoteItem => {
            if (quoteItem.isUpfront) {
                if (quoteItem.isCreditItem) {
                    totalCost -= quoteItem.upfrontCost * quoteItem.quantity
                    totalPrice -= quoteItem.upfrontPrice * quoteItem.quantity
                } else {
                    totalCost += quoteItem.upfrontCost * quoteItem.quantity
                    totalPrice += quoteItem.upfrontPrice * quoteItem.quantity
                }
            }

            if (quoteItem.isRecurring && quoteItem.quantity > 0 && quoteItem.recurringStartMonth > 0 && quoteItem.recurringIntervalMonths > 0 && quoteItem.recurringNumPayments > 0) {
                if (quoteItem.isCreditItem) {
                    totalCost -= quoteItem.recurringCost * quoteItem.recurringNumPayments * quoteItem.quantity
                    totalPrice -= quoteItem.recurringPrice * quoteItem.recurringNumPayments * quoteItem.quantity
                } else {
                    totalCost += quoteItem.recurringCost * quoteItem.recurringNumPayments * quoteItem.quantity
                    totalPrice += quoteItem.recurringPrice * quoteItem.recurringNumPayments * quoteItem.quantity
                }
            }

        });

        setTotalContractProfit("£" + formatCurrency(totalPrice - totalCost))
        setTotalContractRevenue("£" + formatCurrency(totalPrice))
    }

    /*
     *  SAVING QUOTE REVISION BACK TO DB
     */

    // Create debounced DB update function
    const debouncedSaveQuoteRevisionToDB = useCallback(
        debounce((data) => {
            updateQuoteRevisionInDB(data);
            console.log("♻️ Revision saved to DB");

            refreshRevisionLock(data);
        }, 500),
        [] // Created once
    );

    const refreshRevisionLock = (quoteRevision) => {
        const isLocked = quoteRevision?.isLocked;
        if (isLocked !== undefined) {
            // set the lockOverride variable in LockCheck context. So the lock banner will update
            lockState.setLockOverride({ isOn: isLocked, message: isLocked ? 'This quote revision is locked because it has already been generated. To make changes, please create a new revision' : '' });
        }
    }

    // Cleanup on unmount and immediately execute any pending updates
    useEffect(() => {
        return () => {
            debouncedSaveQuoteRevisionToDB.flush();
        };

    }, []);

    useEffect(() => {
        if (!quoteRevision) return

        if (saveRevision.current) {
            debouncedSaveQuoteRevisionToDB(quoteRevision)
        } else {
            refreshRevisionLock(quoteRevision);
            console.log("♻️ Revision lock updated");
            saveRevision.current = true;
        }
    }, [quoteRevision])

    /*
     *  USE EFFECTS
     */

    useEffect(() => {
        if (!quoteRevisionId) return;


        if (quoteRevisions?.length > 0 && quoteRevisionId) {
            setQuoteRevision(quoteRevisions.find(x => x.id === Number(quoteRevisionId)))
        } else {
            setQuoteRevision()
        }
    }, [quoteRevisions, quoteRevisionId]);

    // When the loaded quote revision changes, we need to update the related data also. If the quote was locked then unlocks, it will update too
    useEffect(() => {
        const updateQuoteItemsAndModulesForRevision = async () => {
            setIsLoadingQuoteItems(true);
            var newQuoteItems = await fetchQuoteItems(quoteRevisionId)

            if (newQuoteItems.length === 0) {
                // There are no quote items, so we can stop showing the loading spinner now. 
                setIsLoadingQuoteItems(false);
            } else {
                // If there are quote items, we will stop the loading spinner after we have set them in state
                // by setting it in the useEffect for quoteItemsWithCalculatedProperties, so that the UI doesn't show
                // the quote items until we have calculated properties for them
            }

            if (newQuoteItems) {
                setQuoteItems(newQuoteItems.filter(x => x.source !== "Basket Processing"))
                setAutogeneratedQuoteItems(newQuoteItems.filter(x => x.source === "Basket Processing"))
            } else {
                alert('Failed to find quoteItems for quoteRevision ' + quoteRevisionId)
                setQuoteItems([])
                setAutogeneratedQuoteItems([])
            }

            var newQuoteModules = await fetchQuoteModules(quoteRevisionId)

            console.log('newQuoteModules', newQuoteModules)

            if (newQuoteModules) {
                setQuoteModules(newQuoteModules)
            } else {
                alert('Failed to find quoteModules for quoteRevision ' + quoteRevisionId)
                setQuoteModules([])
            }
        }

        if (quoteRevision?.id) {
            updateQuoteItemsAndModulesForRevision();
            calcTotalContractRevenueAndProfit()
        } else {
            setQuoteItems([])
            setQuoteModules([])
        }
    }, [quoteRevision?.id, lockState.isLocked])

    useEffect(() => {
        if (lockState?.updateClients) {
            // if updateClients is true, refetch the quote revisions because the user who was locking the revision has finalised it and sent a notification.
            // So the 'is_locked' column in quote_revision table is now true and the other users need to know
            setTimeout(() => {
                console.log("♻️ Refetching revisions after update clients notification");
                saveRevision.current = false; // flag to call refreshRevisionLock after refetching revisions in the next line. This is done in quoteRevision useEffect
                refetchQuoteRevisions();
                lockState?.setUpdateClients(false);
            }, 1000)
        }
    }, [lockState?.updateClients]) // listen to updateClients (set in LockCheck component)

    useEffect(() => {
        if (quoteRevisionId) {
            setCurrentActiveMainTab("Overview");
        } else {
            setCurrentActiveMainTab("Revisions");
        }
    }, [quoteRevisionId]);

    // If we have no quoteRevisionId, but there are revisions, redirect to the main one (or the first one if none is marked main)
    useEffect(() => {
        if (quoteRevisionId) return;                            // already on a revision
        if (hasRedirectedRef.current) return;                   // already redirected
        if (isLoadingQuoteRevisions || !quoteData) return;      // wait for data

        if (quoteRevisions?.length > 0) {
            const main = quoteRevisions.find(r => r?.isMain) ?? quoteRevisions[0];
            const targetId = String(main.id);
            // Only navigate if it’s actually different (extra safety)
            hasRedirectedRef.current = true;
            console.log("Redirecting to main quote revision", targetId);
            navigate(
                `/opportunities/${opportunityId}/${quoteId}/${targetId}`,
                { replace: true }
            );
        }
    }, [quoteRevisionId, isLoadingQuoteRevisions, quoteRevisions, quoteData, quoteId, navigate]);

    useEffect(() => {
        refetchQuoteData();
        refetchQuoteRevisions();
    }, [lockState.isLocked])


    const getUsersLookup = async () => {
        const users = await fetchUsers();
        return users.map(user => ({
            ...user,
            fullName: `${user.firstName} ${user.lastName}`
        }))
    }

    const {
        data: usersLookup,
        isLoading: isLoadingUsersLookup,
        error: usersLookupError,
    } = useQuery({
        queryKey: ["quoteUsers"],
        queryFn: getUsersLookup,
        staleTime: 60000
    });


    const getQuoteHistory = async () => {
        const audits = await fetchAuditByFilter(quoteHistoryFilter);
        setQuoteHistoryTotalRecords(audits.totalRecords);
        if (audits.totalRecords === 0) return []

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const {
        data: quoteHistoryData,
        isLoading: isLoadingQuoteHistory,
        error: quoteHistoryError,
        refetch: refetchQuoteHistory,
    } = useQuery({
        queryKey: ["quoteHistory", quoteId],
        queryFn: getQuoteHistory,
        enabled: !!quoteData && !!usersLookup, // Only run when quoteData and usersLookup are available
        staleTime: 0
    });

    useEffect(() => {
        if (usersLookup) {
            refetchQuoteHistory();
        }
    }, [quoteHistoryFilter, usersLookup]);

    // this query will run when users press the 'download' button
    // it will be passed as a prop to the Download component and will fetch all records that meet the filter criteria, not just the current page records
    const getQuoteHistoryQueryForDownload = async () => {
        let downloadFilter = { ...quoteHistoryFilter }
        delete downloadFilter.pageSize
        delete downloadFilter.pageNumber

        setQuoteHistoryDownloadButtonIsEnabled(false);

        const audits = await fetchAuditByFilter(downloadFilter);

        setQuoteHistoryDownloadButtonIsEnabled(true);

        return audits.auditItems.map((audit) => {
            const user = usersLookup.find((user) => user?.userGuid === audit.userGuid);
            return {
                ...audit,
                timestamp: formatDateTime(audit.timestamp),
                user: user ? `${user.firstName} ${user.lastName}` : "Unknown",
            };
        });
    }

    const calcGroupingsForQuoteLineEditing = async (updatedQuoteItems) => {

        if (quoteRevision.isLocked) {
            console.warn("Line editor groupings calc will not run if quote revision is finalised");
            return;
        } else if (lockState && lockState?.isLocked) {
            console.warn("Line editor groupings calc will not run here while the quote is locked by someone else.");
            return;
        }

        const groups = quoteRevision?.quoteCustomisation.groups ?? { upfront: [], recurring: [] }

        var updatedGroups = structuredClone(groups)
        var updatedQuoteItemsClone = structuredClone(updatedQuoteItems)

        const upfrontGroupExistsThatContainsNewFormatItemIds = updatedGroups.upfront.some(group => group.items.some(item => item.lineEditorId));
        const recurringGroupExistsThatContainsNewFormatItemIds = updatedGroups.recurring.some(group => group.items.some(item => item.lineEditorId));

        if (!upfrontGroupExistsThatContainsNewFormatItemIds && !recurringGroupExistsThatContainsNewFormatItemIds &&
             (updatedGroups.upfront.length > 0 || updatedGroups.recurring.length > 0)) {

            const updateGroupItemRefsToUseLineEditorId = (groupList, item, quoteModules) => {
                const groupsContainingItemGuid = groupList.filter(g => 
                    g.items.some(itemRef => itemRef.guid === item.guid)
                );

                for (const group of groupsContainingItemGuid) {
                    const groupIsModuleGroup = quoteModules.some(m => m.guid === group.guid);

                    const itemEntryToEdit = group.items.find(itemRef => 
                        itemRef.guid === item.guid && // Has same Guid.  There could be multiple items with same guid if from module added multiple times
                        (
                            groupsContainingItemGuid.length === 1 || // Only one group contains this item, so it must be here
                            !groupIsModuleGroup || // This item is in a custom group, so keep it there                          
                            group.guid === item.quoteModuleGuid // This is a module group and it is for the same module that the item came from.  Hopefully, a user wouldn't have moved an item from one module group to another, as this will reverse it (but only if they have added the module associated with this item more than once)
                        ) && 
                        !itemRef.lineEditorId
                    );

                    if (itemEntryToEdit) {
                        // Group contains the item and there's no lineEditorId, so update the ref to use lineEditorId

                        itemEntryToEdit.lineEditorId = item.lineEditorId;
                        break;
                    } else {
                        // Group does not contain the item, or already has lineEditorId, so skip
                    }
                }
            }

            updatedQuoteItemsClone.forEach(item => {
                // Go through each item, find the group it's in, and update the ref to contain lineEditorId

                if (item.isUpfront) {
                    updateGroupItemRefsToUseLineEditorId(updatedGroups.upfront, item, quoteModules);
                }

                if (item.isRecurring) {
                    updateGroupItemRefsToUseLineEditorId(updatedGroups.recurring, item, quoteModules);
                }
            });
        }

        // Work out which saved groups each item is in (if any).  Note that quote editing groups are independent
        // of the groups used when adding items to the basket.  Though we will default to being the same when first entering the editing
        // tab.  Note that we have to be careful, because items generated by modules have a fixed guid per item, so
        // if you add the same module twice, the you can have multiple items with the same guid, so we have to also check the 
        // quoteModuleGuid
        updatedQuoteItemsClone.forEach(item => {
            item.upfrontGroupGuid = updatedGroups.upfront.find(group => group.items.some(x => x.lineEditorId 
                    ? x.lineEditorId === item.lineEditorId // If this exists then it is from post Jan 2026 quote item line editor that allows multiple items of same guid, so we use this id rather than guid, as it's guaranteed unique
                    : false
            ))?.guid ?? null

            item.recurringGroupGuid = updatedGroups.recurring.find(group => group.items.some(x => x.lineEditorId 
                    ? x.lineEditorId === item.lineEditorId // If this exists then it is from post Jan 2026 quote item line editor that allows multiple items of same guid, so we use this id rather than guid, as it's guaranteed unique
                    : false
            ))?.guid ?? null

        }); 

        function getDefaultItemVisibility(item, moduleAssociatedWithItem, itemInCatalogue) {
            if (item.isUpfront && item.upfrontPrice !== 0) {
                return true;
            } else if (item.isRecurring && item.recurringPrice !== 0) {
                return true;
            }

            const moduleDisplaySetting = moduleAssociatedWithItem?.settings?.quoteDisplay;
            let visible;

            if (itemInCatalogue) {
                // If item is in catalogue, use its default visibility
                visible = !itemInCatalogue.hideOnQuoteByDefault;
            } else if (moduleAssociatedWithItem) {

                if (moduleDisplaySetting) {
                    // If it's a module item, use module settings

                    visible = moduleDisplaySetting === "showGroupAndChildren";
                } else {
                    // This is from a module without this setting yet (older module).  Default to hiding the item
                    visible = false;
                }
            } else {
                // Not in catalogue, and not from a module.  Default to visible
                visible = true;
            }

            return visible;
        }

        const itemsToAddToItemLookupCache = [];

        async function assignToGroup(item, groupList) {
            // Check to see if we already have a group that can contain this item based on its quoteGroupDescription or moduleGuid
            var group = groupList.find(x =>
                ((item.quoteModuleGuid === null || item.quoteModuleGuid === undefined || item.quoteModuleGuid === "") && x.name === item.quoteGroupDescription) ||
                (item.quoteModuleGuid !== null && x.guid === item.quoteModuleGuid)
            )

            const moduleAssociatedWithItem = item.quoteModuleGuid
                ? quoteDataContext.productModules.find(m => m.id === item.moduleId)
                : item.moduleId
                    ? quoteDataContext.basketProcessingModules.find(m => m.id === item.moduleId)
                    : null;

            let itemInCatalogue = item.itemLookupId ? itemLookupCache.find(i => i.id === item.itemLookupId) : null;

            if (!itemInCatalogue && item.itemLookupId) {
                itemInCatalogue = itemsToAddToItemLookupCache.find(i => i.id === item.itemLookupId);

                if (!itemInCatalogue) {
                    itemInCatalogue = await fetchItemLookup(item.itemLookupId)
                    itemsToAddToItemLookupCache.push(itemInCatalogue);
                } else {
                }
            }

            if (!group) {
                // No group found. If linked to a module (then we will use the same guid as the module, just to keep track)
                // otherwise the guid will be unique

                let showChildren = true;
                let groupIsVisible = true;

                if (moduleAssociatedWithItem) {
                    const display = moduleAssociatedWithItem.settings?.quoteDisplay;

                    switch (display) {
                        case "showGroupAndChildren":
                            groupIsVisible = true;
                            showChildren = true;
                            break;
                        case "hideGroupAndChildren":
                            groupIsVisible = false;
                            showChildren = true; // children visibility should be true if group is hidden, or it will affect the cycling of display modes in the editor
                            break;
                        case "showGroupButHideChildren":
                            groupIsVisible = true;
                            showChildren = false;
                            break;
                        default:
                            // If there is no setting then just use default value that the editor has if setting is null.
                            groupIsVisible = false;
                            showChildren = true; // children visibility should be true if group is hidden, or it will affect the cycling of display modes in the editor
                    }
                }

                const itemIsVisible = getDefaultItemVisibility(item, moduleAssociatedWithItem, itemInCatalogue);

                group = {
                    guid: item.quoteModuleGuid ?? crypto.randomUUID(),
                    name: item.quoteGroupDescription,
                    items: [{ guid: item.guid, lineEditorId: item.lineEditorId, visible: itemIsVisible }],
                    showChildren: showChildren,
                    expanded: false,
                    visible: groupIsVisible
                }

                groupList.push(group)
            } else {
                // Add the item to the group, if it isn't already there (shouldn't be the case, as we checked earlier, but just in case 
                // it's in multiple groups somehow)

                if (!group.items.some(i => i.lineEditorId === item.lineEditorId)) {
                    let itemIsVisible = getDefaultItemVisibility(item, moduleAssociatedWithItem, itemInCatalogue);

                    group.items.push({
                        guid: item.guid,
                        lineEditorId: item.lineEditorId,
                        visible: itemIsVisible
                    });
                } else {
                }
            }

            return group.guid
        }

        // Loop through all items that aren't in a group yet, and create a new group for them which defaults
        // to be the same named group that they are in the build and pricing tabs.
        // If it's a module item then we will use the same guid for the group, as the module had, just to ensure that if the user created a manually
        // added group of the same name, we won't merge them.  At least not automatically.  The user can manually merge them
        // once we've produced the initial list
        for (const item of updatedQuoteItemsClone.filter(x => x.upfrontGroupGuid === null && x.isUpfront)) {
            item.upfrontGroupGuid = await assignToGroup(item, updatedGroups.upfront)
        }

        for (const item of updatedQuoteItemsClone.filter(x => x.recurringGroupGuid === null && x.isRecurring)) {
            item.recurringGroupGuid = await assignToGroup(item, updatedGroups.recurring)
        }

        if (itemsToAddToItemLookupCache.length > 0) {
            // push it into the cache.  timing issues can mean that the item is now there, so we need to check for dupes
            setItemLookupCache(prevCache => {
                const combined = [...prevCache, ...itemsToAddToItemLookupCache];
                const unique = Array.from(new Map(combined.map(i => [i.id, i])).values());
                return unique;
            });
        }

        // This map is indexed by lineEditorId, which is unique per quote item, even for module items that have been added more than once.
        // This is new from Jan 2026, and is the preferred method of searching for items now
        const updatedQuoteItemsMapByLineEditorId = new Map(
            updatedQuoteItemsClone.map(item => [item.lineEditorId, item])
        );


        /*
            Remove items from groups, that are no longer in the quoteItem list, or are assigned to another group
        */

        updatedGroups.upfront.forEach(group => { 
            group.items = group.items.filter(itemMarkedAsStoredInThisGroup => {
                const item = updatedQuoteItemsMapByLineEditorId.get(itemMarkedAsStoredInThisGroup.lineEditorId);

                const isValidForThisGroup = item && // Item is still on the quote
                       item.isUpfront && // Need to check this, as could have same group Guid for upfront and recurring groups if the group was created from a module
                       item.upfrontGroupGuid === group.guid; // It's still in this group (not manually moved to another)

                return isValidForThisGroup;
            });
        });

        updatedGroups.recurring.forEach(group => {
            group.items = group.items.filter(itemMarkedAsStoredInThisGroup => {
                const item = updatedQuoteItemsMapByLineEditorId.get(itemMarkedAsStoredInThisGroup.lineEditorId);

                const isValidForThisGroup = item && // Item is still on the quote
                      item.isRecurring && // Need to check this, as could have same group Guid for upfront and recurring groups if the group was created from a module
                      item.recurringGroupGuid === group.guid; // It's still in this group (not manually moved to another)

                return isValidForThisGroup;
            });
        });

        /*
            Remove groups that contain no items
        */

        updatedGroups.upfront = updatedGroups.upfront.filter(group => group.items.length > 0);
        updatedGroups.recurring = updatedGroups.recurring.filter(group => group.items.length > 0);

        updatedGroups.upfront = updatedGroups.upfront.map(group => updateGroupStateAccordingToChildren(group));
        updatedGroups.recurring = updatedGroups.recurring.map(group => updateGroupStateAccordingToChildren(group));

        console.log("calcGroupingsForQuoteLineEditing: updated groups", updatedGroups)

        updateQuoteRevision("quoteCustomisation", {
            ...quoteRevision.quoteCustomisation,
            groups: updatedGroups
        }
        )
    }

    const handleUpfrontGroupMoved = (guid, newPosition) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            let newUpfront = [...prevCustomisation.groups.upfront]

            const groupToBeMoved = newUpfront.find(x => x.guid === guid)

            if (!groupToBeMoved) {
                return prevCustomisation
            }

            // Remove it from the list and put back in new position
            newUpfront = newUpfront.filter(x => x.guid !== guid)
            newUpfront.splice(newPosition, 0, groupToBeMoved)

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: newUpfront
                }
            };

            return updated;
        });

        console.log("handleUpfrontGroupMoved", guid, newPosition)
    }

    const handleUpfrontGroupExpandedChanged = (guid, expanded) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newUpfront = prevCustomisation.groups.upfront.map(group =>
                group.guid === guid ? { ...group, expanded: expanded } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: newUpfront
                }
            };

            return updated;
        });
    }

    const handleRecurringGroupExpandedChanged = (guid, expanded) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newRecurring = prevCustomisation.groups.recurring.map(group =>
                group.guid === guid ? { ...group, expanded: expanded } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: newRecurring
                }
            };

            return updated;
        });
    }

    const handleRecurringGroupMoved = (guid, newPosition) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            let newRecurring = [...prevCustomisation.groups.recurring]

            const groupToBeMoved = newRecurring.find(x => x.guid === guid)

            if (!groupToBeMoved) {
                return prevCustomisation
            }

            // Remove it from the list and put back in new position
            newRecurring = newRecurring.filter(x => x.guid !== guid)
            newRecurring.splice(newPosition, 0, groupToBeMoved)

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: newRecurring
                }
            };

            return updated;
        });

        console.log("handleRecurringGroupMoved", guid, newPosition)
    }

    const updateGroupStateAccordingToChildren = (group) => {
        let totalUpfrontPrice = 0;
        let totalRecurringPrice = 0;
        let sumOfUpfrontPrice = 0
        let sumOfRecurringPrice = 0
        let sumOfUnitAnnualPrice = 0
        let totalAnnualPrice = 0
        let upfrontPrice
        let recurringPrice
        let annualPrice
        let quantity

        const updatedItems = [...group.items];

        let allQuantities = new Set();

        updatedItems.forEach(groupItem => {
            const quoteItem = allEnabledQuoteItemsWithCalculatedProperties.find(x => x.lineEditorId === groupItem.lineEditorId);

            if (quoteItem) {
                // Safely add to totals (handle NaN or undefined)
                const childUpfront = Number(quoteItem?.totalUpfrontPrice) || 0;
                const childRecurring = Number(quoteItem?.totalRecurringPrice) || 0;

                sumOfUpfrontPrice += Number(quoteItem.upfrontPrice) || 0
                sumOfRecurringPrice += Number(quoteItem.recurringPrice) || 0

                sumOfUnitAnnualPrice += Number(quoteItem.annualPrice) || 0
                totalAnnualPrice += Number(quoteItem.annualPrice * quoteItem.quantity) || 0

                totalUpfrontPrice += childUpfront;
                totalRecurringPrice += childRecurring;

                allQuantities.add(quoteItem.quantity);
            }
        });

        if (allQuantities.size === 1) {
            // If all child items have the same quantity, then add up the total
            quantity = [...allQuantities][0];
            upfrontPrice = sumOfUpfrontPrice
            recurringPrice = sumOfRecurringPrice
            annualPrice = sumOfUnitAnnualPrice
        } else {
            // If different quantities, then quanity is 1, and we need the unit price to be the same as total price
            quantity = 1
            upfrontPrice = totalUpfrontPrice
            recurringPrice = totalRecurringPrice
            annualPrice = totalAnnualPrice
        }

        // Override showOnQuote if any child has a non-zero price
        const anyChildHasPrice = totalUpfrontPrice > 0 || totalRecurringPrice > 0;

        return {
            ...group,
            quantity,
            totalUpfrontPrice,
            totalRecurringPrice,
            totalAnnualPrice,
            annualPrice,
            upfrontPrice,
            recurringPrice,
            visible: anyChildHasPrice ? true : group.visible
        };
    }

    const handleUpfrontItemMoved = ({ itemId, fromGroupId, toGroupId, newPosition }) => {

        updateQuoteRevision("quoteCustomisation", prevCustomisation => {

            var sourceGroup = prevCustomisation.groups.upfront.find(x => x.guid === fromGroupId)
            var destinationGroup = prevCustomisation.groups.upfront.find(x => x.guid === toGroupId)
            const quoteItemFromBasket = allQuoteItemsWithCalculatedProperties.find(x => x.lineEditorId === itemId);
            var itemToBeMoved = sourceGroup.items.find(x => x.lineEditorId === quoteItemFromBasket?.lineEditorId)

            if (!sourceGroup || !destinationGroup || !itemToBeMoved) {
                // current state doesn't have the requested groups, or the item to be moved, so do nothing
                console.log("handleUpfrontItemMoved failure - Source, Destination or ItemId not found", itemId, fromGroupId, toGroupId, newPosition)

                return prevCustomisation;
            } else if (!quoteItemFromBasket) {
                // item being moved is in the model for the line editor, but it's not in the basket any more, so just remove it
                // from the line editor model

                console.log("handleUpfrontItemMoved failure - Basket Item not found", itemId, fromGroupId, toGroupId, newPosition)

                return {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        upfront: prevCustomisation.groups.upfront.map(upfrontGroup => ({
                            ...upfrontGroup,
                            items: upfrontGroup.items.filter(item => item.lineEditorId !== itemId)
                        }))
                    }
                }
            } else {
                const newUpfrontGroups = prevCustomisation.groups.upfront.map(group => {

                    if (group.guid === fromGroupId && group.guid === toGroupId) {
                        // ✅ Moving inside the same group — reorder the items

                        console.log("Moving item within group:", group.name);

                        let updatedGroup = {
                            ...group,
                            items: group.items.filter(x => x.lineEditorId ? x.lineEditorId !== itemToBeMoved.lineEditorId : x.guid !== itemToBeMoved.guid) // Remove the item from source group
                        };

                        if (!itemToBeMoved.lineEditorId) {
                            // Upgrade item that was moved to new format (Jan 2026) that supports multiple items of same guid (due to adding same module twice)
                            itemToBeMoved.lineEditorId = itemId; 
                        }

                        // Put it back in the correct position
                        updatedGroup.items.splice(newPosition, 0, itemToBeMoved);

                        return updatedGroup
                    } else if (group.guid === fromGroupId) {
                        console.log("Removing item from source group:", group.name);

                        let updatedGroup = {
                            ...group,
                            items: group.items.filter(x => x.lineEditorId ? x.lineEditorId !== itemToBeMoved.lineEditorId : x.guid !== itemToBeMoved.guid) // Remove the item from source group
                        };

                        updatedGroup = updateGroupStateAccordingToChildren(updatedGroup);

                        return updatedGroup
                    } else if (group.guid === toGroupId) {
                        console.log("Adding item to destination group:", group.name);

                        const newItems = [...group.items];

                        if (!itemToBeMoved.lineEditorId) {
                            // Upgrade item that was moved to new format (Jan 2026) that supports multiple items of same guid (due to adding same module twice)
                            itemToBeMoved.lineEditorId = itemId; 
                        }

                        // Add item to destination group in the correct position
                        newItems.splice(newPosition, 0, itemToBeMoved);

                        let updatedGroup = {
                            ...group,
                            items: newItems,
                            expanded: true, // Expand the target branch, if it wasn't already) so you can see the item in place
                            visible: quoteItemFromBasket.upfrontPrice > 0 || group.visible  // If you are moving an item with a price, ensure that the target group is visible
                        };

                        // Recalculate totals and visibility
                        updatedGroup = updateGroupStateAccordingToChildren(updatedGroup);

                        return updatedGroup;
                    }

                    return group;
                });

                console.log("handleUpfrontItemMoved success", itemId, fromGroupId, toGroupId, newPosition)

                return {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        upfront: newUpfrontGroups
                    }
                }
            }
        });

    }

    const handleRecurringItemMoved = ({ itemId, fromGroupId, toGroupId, newPosition }) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {

            var sourceGroup = prevCustomisation.groups.recurring.find(x => x.guid === fromGroupId)
            var destinationGroup = prevCustomisation.groups.recurring.find(x => x.guid === toGroupId)
            const quoteItemFromBasket = allQuoteItemsWithCalculatedProperties.find(x => x.lineEditorId === itemId);
            var itemToBeMoved = sourceGroup.items.find(x => x.lineEditorId === quoteItemFromBasket?.lineEditorId)

            if (!sourceGroup || !destinationGroup || !itemToBeMoved) {
                // current state doesn't have the requested groups, or the item to be moved, so do nothing
                console.log("handleRecurringItemMoved failure - Source, Destination or ItemId not found", itemId, fromGroupId, toGroupId, newPosition)

                return prevCustomisation;
            } else if (!quoteItemFromBasket) {
                // item being moved is in the model for the line editor, but it's not in the basket any more, so just remove it
                // from the line editor model

                console.log("handleRecurringItemMoved failure - Basket Item not found", itemId, fromGroupId, toGroupId, newPosition)

                return {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        recurring: prevCustomisation.groups.recurring.map(recurringGroup => ({
                            ...recurringGroup,
                            items: recurringGroup.items.filter(item => item.lineEditorId !== itemId)
                        }))
                    }
                }
            } else {
                const newRecurringGroups = prevCustomisation.groups.recurring.map(group => {

                    if (group.guid === fromGroupId && group.guid === toGroupId) {
                        // ✅ Moving inside the same group — reorder the items

                        console.log("Moving item within group:", group.name);

                        let updatedGroup = {
                            ...group,
                            items: group.items.filter(x => x.lineEditorId ? x.lineEditorId !== itemToBeMoved.lineEditorId : x.guid !== itemToBeMoved.guid) // Remove the item from source group
                        };

                        // Put it back in the correct position
                        updatedGroup.items.splice(newPosition, 0, itemToBeMoved);

                        return updatedGroup
                    } else if (group.guid === fromGroupId) {
                        console.log("Removing item from source group:", group.name);

                        let updatedGroup = {
                            ...group,
                            items: group.items.filter(x => x.lineEditorId ? x.lineEditorId !== itemToBeMoved.lineEditorId : x.guid !== itemToBeMoved.guid) // Remove the item from source group
                        };

                        updatedGroup = updateGroupStateAccordingToChildren(updatedGroup);

                        return updatedGroup
                    } else if (group.guid === toGroupId) {
                        console.log("Adding item to destination group:", group.name);

                        const newItems = [...group.items];

                        // Add item to destination group in the correct position
                        newItems.splice(newPosition, 0, itemToBeMoved);

                        let updatedGroup = {
                            ...group,
                            items: newItems,
                            expanded: true, // Expand the target branch, if it wasn't already) so you can see the item in place
                            visible: quoteItemFromBasket.recurringPrice > 0 || group.visible  // If you are moving an item with a price, ensure that the target group is visible
                        };

                        // Recalculate totals and visibility
                        updatedGroup = updateGroupStateAccordingToChildren(updatedGroup);

                        return updatedGroup;
                    }

                    return group;
                });

                console.log("handleRecurringItemMoved success", itemId, fromGroupId, toGroupId, newPosition)

                return {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        recurring: newRecurringGroups
                    }
                }
            }
        });

    }

    const handleEditUpfrontGroup = (guid, newName) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newUpfront = prevCustomisation.groups.upfront.map(group =>
                group.guid === guid ? { ...group, name: newName } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: newUpfront
                }
            };

            const prevGroupName = prevCustomisation.groups.upfront.find(x => x.guid === guid)?.name ?? ""
            if (newName !== prevGroupName) {
                addAudit({ action: "Group name changed", entity: "quote", entityId: quoteRevision.quoteId, payload: { upfrontGroup: { guid: guid, oldName: prevGroupName, newName: newName } } })
            }

            return updated;
        });
        console.log("handleEditUpfrontGroup", newName)
    }

    const handleEditRecurringGroup = (guid, newName) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newRecurring = prevCustomisation.groups.recurring.map(group =>
                group.guid === guid ? { ...group, name: newName } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: newRecurring
                }
            };

            const prevGroupName = prevCustomisation.groups.recurring.find(x => x.guid === guid)?.name ?? ""
            if (newName !== prevGroupName) {
                addAudit({ action: "Group name changed", entity: "quote", entityId: quoteRevision.quoteId, payload: { recurringGroup: { guid: guid, oldName: prevGroupName, newName: newName } } })
            }

            return updated;
        });

        console.log("handleEditRecurringGroup", newName)
    }

    const handleAddUpfrontGroup = (groupName) => {
        const newGroup =
        {
            guid: crypto.randomUUID(),
            name: groupName,
            items: [],
            showChildren: true,
            visible: true
        }

        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: [...prevCustomisation.groups.upfront, newGroup]
                }
            };

            return updated;
        });

        addAudit({ action: "Group created", entity: "quote", entityId: quoteRevision.quoteId, payload: { upfrontGroup: { guid: newGroup.guid, name: groupName } } })

        console.log("handleAddUpfrontGroup", groupName)
    }


    const handleAddRecurringGroup = (groupName) => {
        const newGroup =
        {
            guid: crypto.randomUUID(),
            name: groupName,
            items: [],
            showChildren: true,
            visible: true
        }

        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: [...prevCustomisation.groups.recurring, newGroup]
                }
            };

            return updated;
        });

        addAudit({ action: "Group created", entity: "quote", entityId: quoteRevision.quoteId, payload: { recurringGroup: { guid: newGroup.guid, name: groupName } } })

        console.log("handleAddRecurringGroup", groupName)
    }

    const handleUpdateUpfrontShowChildren = (guid, showChildren) => {

        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newUpfront = prevCustomisation.groups.upfront.map(group =>
                group.guid === guid ? { ...group, showChildren } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: newUpfront
                }
            };

            const groupName = newUpfront.find(x => x.guid === guid)?.name ?? ""
            addAudit({ action: "Group visibility updated", entity: "quote", entityId: quoteRevision.quoteId, payload: { upfrontGroup: { id: guid, name: groupName, visible: showChildren } } })

            return updated;
        });

        console.log("handleUpdateUpfrontShowChildren", guid, showChildren)
    }

    const handleUpdateRecurringShowChildren = (guid, showChildren) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const newRecurring = prevCustomisation.groups.recurring.map(group =>
                group.guid === guid ? { ...group, showChildren } : group
            );

            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: newRecurring
                }
            };

            const groupName = newRecurring.find(x => x.guid === guid)?.name ?? ""
            addAudit({ action: "Group visibility updated", entity: "quote", entityId: quoteRevision.quoteId, payload: { recurringGroup: { id: guid, name: groupName, visible: showChildren } } })

            return updated;
        });

        console.log("handleUpdateRecurringShowChildren", guid, showChildren)
    }

    const handleUpdateUpfrontVisibleGroupOrItem = (id, visible) => {
        let group = quoteRevision.quoteCustomisation.groups.upfront.find(f => f.guid === id);

        if (group) {
            // This is a group

            updateQuoteRevision("quoteCustomisation", prevCustomisation => {
                const newUpfront = prevCustomisation.groups.upfront.map(group =>
                    group.guid === id ? { ...group, visible } : group
                );

                const updated = {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        upfront: newUpfront
                    }
                };

                return updated;
            });

            addAudit({ action: "Group visibility updated", entity: "quote", entityId: quoteRevision.quoteId, payload: { upfrontGroup: { id: id, name: group.name, visible: visible } } })
        } else {
            // This is not a group, so hopefuly it's a child item!   

            const quoteItem = allQuoteItemsWithCalculatedProperties.find(x => x.lineEditorId === id)

            if (quoteItem) {

                updateQuoteRevision("quoteCustomisation", prevCustomisation => {
                    const newUpfront = prevCustomisation.groups.upfront.map(group =>
                        group.items.some(item => item.lineEditorId === id) // if this group contains the item
                            ? {
                                ...group,
                                visible: visible ? true : group.visible, // if making visible, make parent visible too, otherwise leave as is
                                items: group.items.map(item => item.lineEditorId === id
                                    ? { ...item, visible: visible } // Set the item visibility
                                    : item // Not the item, leave as is
                                )
                            }
                            : group // Not the group, leave as is
                    );

                    const updated = {
                        ...prevCustomisation,
                        groups: {
                            ...prevCustomisation.groups,
                            upfront: newUpfront
                        }
                    };

                    return updated;
                });

                updateQuoteItem({ ...quoteItem, showOnQuote: visible })
            }
        }
    }


    const handleUpdateRecurringVisibleGroupOrItem = (id, visible) => {
        let group = quoteRevision.quoteCustomisation.groups.recurring.find(f => f.guid === id);

        if (group) {
            // This is a group

            updateQuoteRevision("quoteCustomisation", prevCustomisation => {
                const newRecurring = prevCustomisation.groups.recurring.map(group =>
                    group.guid === id ? { ...group, visible } : group
                );

                const updated = {
                    ...prevCustomisation,
                    groups: {
                        ...prevCustomisation.groups,
                        recurring: newRecurring
                    }
                };

                return updated;
            });

            addAudit({ action: "Group visibility updated", entity: "quote", entityId: quoteRevision.quoteId, payload: { recurringGroup: { id: id, name: group.name, visible: visible } } })
        } else {
            // This is not a group, so hopefuly it's a child item!   

            const quoteItem = allQuoteItemsWithCalculatedProperties.find(x => x.lineEditorId === id)

            if (quoteItem) {

                updateQuoteRevision("quoteCustomisation", prevCustomisation => {
                    const newRecurring = prevCustomisation.groups.recurring.map(group =>
                        group.items.some(item => item.lineEditorId === id) // if this group contains the item
                            ? {
                                ...group,
                                visible: visible ? true : group.visible, // if making visible, make parent visible too, otherwise leave as is
                                items: group.items.map(item => item.lineEditorId === id
                                    ? { ...item, visible: visible } // Set the item visibility
                                    : item // Not the item, leave as is
                                )
                            }
                            : group // Not the group, leave as is
                    );

                    const updated = {
                        ...prevCustomisation,
                        groups: {
                            ...prevCustomisation.groups,
                            recurring: newRecurring
                        }
                    };

                    return updated;
                });

                updateQuoteItem({ ...quoteItem, showOnQuote: visible })
            }
        }
    }

    const handleDeleteUpfrontGroup = (guid) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    upfront: prevCustomisation.groups.upfront.filter(x => x.guid !== guid)
                }
            };

            const groupName = prevCustomisation.groups.upfront.find(x => x.guid === guid)?.name ?? ""
            addAudit({ action: "Group deleted", entity: "quote", entityId: quoteRevision.quoteId, payload: { upfrontGroup: { guid: guid, name: groupName } } })

            return updated;
        });

        console.log("handleDeleteUpfrontGroup", guid)
    }

    const handleDeleteRecurringGroup = (guid) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const updated = {
                ...prevCustomisation,
                groups: {
                    ...prevCustomisation.groups,
                    recurring: prevCustomisation.groups.recurring.filter(x => x.guid !== guid)
                }
            };

            const groupName = prevCustomisation.groups.recurring.find(x => x.guid === guid)?.name ?? ""
            addAudit({ action: "Group deleted", entity: "quote", entityId: quoteRevision.quoteId, payload: { recurringGroup: { guid: guid, name: groupName } } })

            return updated;
        });

        console.log("handleDeleteRecurringGroup", guid)
    }

    const handleRevisionNotesChanged = (savedVal) => {
        setQuoteRevision(prev => {
            const updated = { ...prev, additionalInformation: savedVal }
            return updated;
        });
    }

    const getModuleGroupDescription = (quoteModule) => {
        const groupDescriptionEntry = quoteModule.inputValues.find(x => x.inputGuid === "quoteGroupDescription")
        return groupDescriptionEntry ? groupDescriptionEntry.value : ""
    }

    const auditItemUpdate = (updatedBasketItem) => {
        // We can have multiple items with same guid if they are from a module that has been added more than once,
        // so we need to also check quoteModuleGuid to find the correct one
        const quoteItem = allQuoteItemsWithCalculatedProperties.find(x => 
            x.guid === updatedBasketItem.guid &&
            ( x.quoteModuleGuid === null || x.quoteModuleGuid === updatedBasketItem.quoteModuleGuid) 
        )

        if (quoteItem) {
            let diffs = findChangesInObject(quoteItem, updatedBasketItem, true, false)
            if (Object.keys(diffs).length === 0) return

            const currentQuoteRevision = quoteRevisionRef.current;
            diffs = { revisionNumber: currentQuoteRevision.revisionNumber, item: { id: updatedBasketItem.itemLookupId ?? updatedBasketItem.id, internalName: updatedBasketItem.internalName, nameOnQuote: updatedBasketItem.isUpfront && updatedBasketItem.isRecurring ? `${updatedBasketItem.upfrontNameOnQuote} / ${updatedBasketItem.recurringNameOnQuote}` : updatedBasketItem.isUpfront ? updatedBasketItem.upfrontNameOnQuote : updatedBasketItem.recurringNameOnQuote }, ...diffs }
            addAudit({ action: "Quote item updated", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: diffs })
        }
    }

    const auditItemsAdd = (items) => {
        if (items.length > 0) {
            const currentQuoteRevision = quoteRevisionRef.current;
            addAudit({ action: "Quote items added", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: {group: items[0].quoteGroupDescription, numItems: items.length, items} })
        }
    }

    const auditItemsUpdate = (items) => {
        if (items.length > 0) {

            const payload = []

            for (const updatedBasketItem of items) {

                // We can have multiple items with same guid if they are from a module that has been added more than once,
                // so we need to also check quoteModuleGuid to find the correct one
                const quoteItem = allQuoteItemsWithCalculatedProperties.find(x => 
                    x.guid === updatedBasketItem.guid &&
                    ( x.quoteModuleGuid === null || x.quoteModuleGuid === updatedBasketItem.quoteModuleGuid) 
                )

                if (quoteItem) {

                    const diffs = findChangesInObject(quoteItem, updatedBasketItem, true, false)
                    if (Object.keys(diffs).length > 0) {
                        payload.push({ id: updatedBasketItem.id, internalName: updatedBasketItem.internalName.trim(), diffs })
                    }
                }
            }

            const currentQuoteRevision = quoteRevisionRef.current;
            addAudit({ action: "Quote items updated", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: payload})
        }
    }

    const auditModuleInsertion = (quoteModuleToInsert) => {
        const groupDescription = getModuleGroupDescription(quoteModuleToInsert)
        const currentQuoteRevision = quoteRevisionRef.current;
        addAudit({ action: "Module added to quote", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: { module: { id: quoteModuleToInsert.moduleId, name: groupDescription } } })
    }

    const auditModuleDeletion = (quoteModuleToDelete) => {
        const groupDescription = getModuleGroupDescription(quoteModuleToDelete)
        const currentQuoteRevision = quoteRevisionRef.current;
        addAudit({ action: "Module deleted from quote", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: { module: { id: quoteModuleToDelete.moduleId, name: groupDescription } } })
    }

    const auditModuleUpdate = (updatedBasketModule, moduleRevisionFormInputs) => {
        const quoteModule = quoteModules.find(x => x.guid === updatedBasketModule.guid)

        if (quoteModule) {
            let diffs = findChangesInModuleObject(quoteModule, updatedBasketModule, moduleRevisionFormInputs)
            if (Object.keys(diffs).length === 0) return

            const currentQuoteRevision = quoteRevisionRef.current;
            const groupDescription = getModuleGroupDescription(updatedBasketModule)
            diffs = { revisionNumber: currentQuoteRevision.revisionNumber, module: { id: updatedBasketModule.moduleId, name: groupDescription }, ...diffs }

            addAudit({ action: "Quote module updated", entity: "quote", entityId: currentQuoteRevision.quoteId, payload: diffs })
        }
    }

    const addAudit = (data) => {
        createAudit({ "action": data.action, "result": "Successful", "source": `${data.entity}.${data.entityId}`, "payload": JSON.stringify(data.payload), ...(data.notes && { notes: data.notes }) })
    }

    useEffect(() => {
        if (quoteRevision) {
            calcTotalContractRevenueAndProfit()
            calcGroupingsForQuoteLineEditing(allQuoteItemsWithCalculatedProperties)
        }
    }, [allQuoteItemsWithCalculatedProperties])

    const handleBasketProcessingInputValueChanged = (moduleId, inputGuid, newValue) => {
        updateQuoteRevision("quoteCustomisation", prevCustomisation => {
            const prevInputs = prevCustomisation.basketProcessingInputValues || [];
            let newInputs = [...prevInputs];

            const inputValueIndex = newInputs.findIndex(x => x.inputGuid === inputGuid);

            if (inputValueIndex >= 0) {
                const currentInputValue = newInputs[inputValueIndex];
                newInputs[inputValueIndex] = { ...currentInputValue, value: newValue };
            } else {
                newInputs.push({ inputGuid, value: newValue });
            }

            return {
                ...prevCustomisation,
                basketProcessingInputValues: newInputs, // replaced with new reference
            };
        });
    };

    //dropdown-controls
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggleDropdown = () => {
        setDropdownOpen(!dropdownOpen);
    };


    const changeQuoteHistoryFilter = (obj) => {
        const filterId = obj.filterId
        const value = obj.value

        if (filterId === "pageNumber") {
            setQuoteHistoryFilter(prev => ({ ...prev, [filterId]: value }));
        } else if (filterId === "searchTerm") {
            if (value === '') {
                setQuoteHistoryFilter(({ searchOptions, ...rest }) => ({ ...rest, pageNumber: 1 }));
            } else {
                let searchOptions = { searchableFields: quoteHistorySearchableFields, searchTerm: value, fieldSearchTerms: [] }

                // try to find the search parameter in usersLookup (search parameter can be the user's full name)
                // if found, add the userGuids to the searchOptions
                if (quoteHistorySearchableFields.find(w => w === "user") && usersLookup) {
                    const userGuids = usersLookup.filter(w => w.fullName?.toLowerCase().includes(value?.toLowerCase())).map(u => u.userGuid);
                    if (userGuids.length > 0) {
                        searchOptions.fieldSearchTerms.push({ "fieldName": "user", "values": userGuids })
                    }
                }

                setQuoteHistoryFilter(prev => ({ ...prev, searchOptions: searchOptions, pageNumber: 1 }));
            }
        } else {
            if (!value) {
                setQuoteHistoryFilter(prev => {
                    const { [obj.filterId]: _, ...rest } = prev;
                    return { ...rest, pageNumber: 1 };
                });
            } else {
                setQuoteHistoryFilter(prev => ({ ...prev, [filterId]: value, pageNumber: 1 }));
            }
        }

        if (quoteRevision) setQuoteHistoryFilter(prev => ({ ...prev, source: `quote.${quoteRevision.quoteId}` }));
    }

    const checkIfFolderExists = async (domain, relativePath, quoteId) => {
        const { message, responseOk } = await verifyFolderExists(domain, relativePath, quoteId)
        if (!responseOk) {
            toast.error(`Error checking SharePoint folder existence. Folder: ${relativePath}. Message: ${message}`);            
            return { responseOk, folderExists: false };
        }

        return { responseOk, folderExists: message };
    }


    // --- Quote Modal ---
    const [quoteModalOpen, setQuoteModalOpen] = useState(false);

    const opportunityData = {
        Id: quoteData?.salesforceOpportunityId,
        OpportunityName: quoteData?.salesforceOpportunityName,
        AccountName: quoteData?.salesforceAccountName,
        IsActive: quoteData?.isActive
    };

    if (isLoadingQuoteData && isLoadingQuoteRevisions) return <DataLoader message="Loading quote data..." />;
    if (quoteDataError && quoteRevisionsError) return <DataLoaderFailed message="Failed to load quote data." />;

    return (
        <div style={{ minHeight: currentActiveMainTab === "Editor" ? 0 : '100vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: currentActiveMainTab === "Editor" ? '0 0 auto' : '1 0 auto', paddingBottom: currentActiveMainTab === "Editor" && !showPageFooter ? 0 : '80px' }}>
                <LockedBanner entityName="revision" />

            <Row className="align-items-start justify-content-between">
                <Col>
                    <HeaderWithIcon icon="bi bi-file-earmark-spreadsheet">
                        {quoteData?.isActive && <i className="bi bi-star-fill me-2" title="Active Quote" />}
                        {quoteData?.name}                   
                        {opportunityState !== "Unknown" && <span className="ms-2">
                                <span className="opacity-50">({opportunityState}</span>
                                {opportunityUpdatesAllowed ?
                                    <i className={`bi ${["Error", "Failed"].includes(salesforceSync.salesforceSyncState) ? "bi-exclamation-triangle text-danger" : salesforceSync.salesforceSyncState === "Warning" ? "bi-exclamation-triangle text-warning" : !["Idle", "Initialising"].includes(salesforceSync.salesforceSyncState) ? "bi-arrow-repeat bi-spin text-primary" : "bi-cloud-arrow-up text-primary"} ms-1 opacity-50`} style={{display: 'inline-block', width: '1em', height: '1em', textAlign: 'center', lineHeight: '1em'}} title={["Error", "Failed"].includes(salesforceSync.salesforceSyncState) ? `Salesforce Sync unavailable - ${salesforceSync.salesforceSyncMessage}` : salesforceSync.salesforceSyncState === "Warning" ? `Salesforce Sync warning - ${salesforceSync.salesforceSyncMessage}` : !["Idle", "Initialising"].includes(salesforceSync.salesforceSyncState) ? `Syncing with Salesforce (${salesforceSync.salesforceSyncState}) ` : `Salesforce Sync is enabled`} />
                                    :
                                    <i className="bi bi-lock ms-1 text-danger opacity-50" title={`Salesforce Sync is disabled (${salesforceSyncDisabledReason})`} />
                                }
                                <span className="opacity-50">)</span>
                            </span>
                        }
                        {opportunityState === "Unknown" && <span className="ms-2">
                            <i className="bi bi-cloud-download text-muted opacity-50" title="Reading opportunity state from Salesforce..." />
                        </span>
                        }
                        <i className={`bi bi-pencil ms-2 icon-button ${lockState?.isLocked ? "locked" : ""} "`} style={{ fontSize: "var(--bs-body-font-size)" }} onClick={() => setQuoteModalOpen(true)} />
                    </HeaderWithIcon>
                    {quoteRevision ?
                        <ReducedBreadcrumb title={currentActiveMainTab === "Revisions" ? quoteRevisionId ? `${quoteRevision.name}` : `${quoteRevision.quoteId}` : `${quoteRevision.name}`} />
                        :
                        <ReducedBreadcrumb title={`No revision selected`} />
                    }

                </Col>

                <Col xs="auto" className="d-flex flex-column align-items-end">

                    {quoteRevisionId &&
                        <Dropdown isOpen={dropdownOpen} toggle={toggleDropdown} className="mb-2">
                            <DropdownToggle caret color="secondary">
                                {quoteRevision ? (
                                    <>
                                        {quoteRevision?.isMain && <i className="bi bi-star-fill me-1" />}
                                        {quoteRevision?.revisionNumber}
                                    </>
                                ) : (
                                    `Revision id ${quoteRevisionId} not found`
                                )}
                            </DropdownToggle>

                            <DropdownMenu flip className="text-end">
                                {[...quoteRevisions]
                                    .sort((a, b) => {
                                        // Main revision first
                                        if (a?.isMain && !b?.isMain) return -1;
                                        if (!a?.isMain && b?.isMain) return 1;

                                        // Then by id descending (newest first)
                                        return b.id - a.id;
                                    })
                                    .map((rev) => (
                                        <DropdownItem
                                            key={rev.id}
                                            active={String(rev.id) === String(quoteRevisionId)}
                                            onClick={() => navigate(`/opportunities/${opportunityId}/${quoteId}/${rev.id}`)}
                                        >
                                            {rev?.isMain && <i className="bi bi-star-fill me-1" />}
                                            {rev?.revisionNumber}
                                        </DropdownItem>
                                    ))}
                            </DropdownMenu>
                        </Dropdown>
                    }

                    {/* Cost / income */}
                    <h1 className="display-5 fw-bold text-end">
                        {totalContractRevenue}/<span className="text-primary">{totalContractProfit}</span>
                    </h1>
                </Col>
            </Row >
            <Row>
                <Col style={{ display: "inline-flex", alignItems: "center" }}>
                    {[...(quoteRevisionId ? ["Overview", "Build", "Pricing", "Editor", "Exports", "Review"] : []), "Revisions"].map((tab) => (
                        <h6
                            key={tab}
                            className={
                                currentActiveMainTab === tab
                                    ? "custom-nav-selected custom-nav m-3"
                                    : "custom-nav m-3"
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => setCurrentActiveMainTab(tab)}
                        >
                            {tab}
                        </h6>
                    ))}
                    {quoteRevisionId && <span style={{ opacity: 0.3 }}>|</span>}
                    {[...(quoteRevisionId ? ["Additional Information"] : [])].map((tab) => (
                        <h6
                            key={tab}
                            className={
                                currentActiveMainTab === tab
                                    ? "custom-nav-selected custom-nav m-3"
                                    : "custom-nav m-3"
                            }
                            style={{ cursor: "pointer" }}
                            onClick={() => setCurrentActiveMainTab(tab)}
                        >
                            {tab}
                        </h6>
                    ))}
                </Col>
                <Col className="text-end">
                    <Button onClick={async () => {
                        const sharepointUrl = quoteDataContext.salesforceFields.Opportunity.find(f => f.name === "SharePoint_Folder__c")?.value
                        const result = await validateAndNormaliseUrl(sharepointUrl, quoteId);
                        if (!result?.isValid || result?.isValid === undefined)
                        {
                            const errorMessage = result?.reason ? result.reason : result
                            toast.error(errorMessage);
                            return;
                        }

                        const domain = 'https://azzurricommunications.sharepoint.com'
                        const parsedPath = result?.resolvedUrl
                        const revision = quoteRevisions.find(f => f.id === Number(quoteRevisionId));
                        if (!quoteRevisionId || !revision) {
                            toast.error("Please select a revision before opening Sharepoint.");
                            return;
                        }

                        // check if the revision folder exists using the new revision folder nomenclature, i.e., Quote {quoteId}/Revision {revisionNumber}
                        let relativePath = `${parsedPath}/Draft/Quote ${quoteId}/Revision ${revision.revisionNumber}`
                        const { folderExists, responseOk } = await checkIfFolderExists(domain, relativePath, quoteId)
                        if (!responseOk) return;

                        if (!folderExists) {
                            // fallback: check if the revision folder exists using the old revision folder nomenclature, i.e., {quoteId}/{revisionNumber}
                            relativePath = `${parsedPath}/Draft/${quoteId}/${revision.revisionNumber}`    
                            const { folderExists, responseOk } = await checkIfFolderExists(domain, relativePath, quoteId)
                            if (!responseOk) return;

                            if (!folderExists) {
                                toast.error(`We couldn't find this SharePoint location. Please check the SharePoint folder link in Salesforce and try again.`);
                                return;
                            }
                        }

                        // open the revision folder in browser
                        window.open(encodeURI(`${domain}${relativePath}`), '_blank', 'noopener,noreferrer');
                    }}>Sharepoint</Button>
                    <h6
                        className={
                            currentActiveMainTab === "History"
                                ? "custom-nav-selected custom-nav m-3"
                                : "custom-nav m-3"
                        }
                        style={{ cursor: "pointer", display: "inline-flex" }}
                        onClick={() => { refetchQuoteHistory(); setCurrentActiveMainTab("History") }}
                    >
                        Change History
                    </h6>
                </Col>
            </Row>
            {isLoadingQuoteItems && ["Overview", "Build", "Pricing", "Editor", "Exports", "Review"].includes(currentActiveMainTab) &&
                <DataLoader />
            }
            {
                currentActiveMainTab === "Overview" && !isLoadingQuoteItems && (
                    <QuoteOverview quote={quoteData} quoteRevision={quoteRevision} quoteItems={allQuoteItemsWithCalculatedProperties} salesforceFields={groupedSalesforceFields}  />
                )
            }
            {
                currentActiveMainTab === "Build" && !isLoadingQuoteItems && (
                    <QuoteBuilder
                        quoteRevisionId={quoteRevisionId}
                        quoteItems={allQuoteItemsWithCalculatedProperties}
                        activeBasketProcessingModules={activeBasketProcessingModules}
                        basketProcessingInputValues={quoteRevision?.quoteCustomisation?.basketProcessingInputValues || []}
                        handleBasketProcessingInputValueChanged={handleBasketProcessingInputValueChanged}
                        addQuoteItems={addQuoteItems} addQuoteItem={addQuoteItem} deleteQuoteItem={deleteQuoteItem} updateQuoteItem={updateQuoteItem} deleteQuoteItems={deleteQuoteItems} updateQuoteItems={updateQuoteItems} updateQuoteItemTypeAttribute={updateQuoteItemTypeAttribute}
                        quoteModules={quoteModules}
                        addQuoteModule={addQuoteModule} deleteQuoteModule={deleteQuoteModule} updateQuoteModule={updateQuoteModule}
                        decoupleQuoteModule={decoupleQuoteModule}
                    />
                )
            }
            {
                currentActiveMainTab === "Pricing" && !isLoadingQuoteItems && (
                    <QuoteCommercials
                        quoteModules={quoteModules}
                        quoteItems={allQuoteItemsWithCalculatedProperties}
                        addQuoteItem={addQuoteItem} deleteQuoteItem={deleteQuoteItem} updateQuoteItem={updateQuoteItem} updateQuoteItems={updateQuoteItems}
                    />
                )
            }
            {
                currentActiveMainTab === "Editor" && !isLoadingQuoteItems && (
                    <>
                        {!(quoteRevision?.quoteCustomisation) && (
                            <p>No items available yet.</p>
                        )}

                        {quoteRevision?.quoteCustomisation && (
                            <QuoteEditor
                                quoteRevision={quoteRevision}
                                quoteCustomisation={{
                                    ...(quoteRevision.quoteCustomisation ?? {}),
                                    groups: {
                                        upfront: Array.isArray(quoteRevision?.quoteCustomisation?.groups?.upfront)
                                            ? quoteRevision.quoteCustomisation.groups.upfront
                                            : [],
                                        recurring: Array.isArray(quoteRevision?.quoteCustomisation?.groups?.recurring)
                                            ? quoteRevision.quoteCustomisation.groups.recurring
                                            : [],
                                    },
                                }}
                                quoteItems={allEnabledQuoteItemsWithCalculatedProperties}
                                onUpfrontGroupMoved={handleUpfrontGroupMoved}
                                onUpfrontItemMoved={handleUpfrontItemMoved}
                                onAddUpfrontGroup={handleAddUpfrontGroup}
                                onDeleteUpfrontGroup={handleDeleteUpfrontGroup}
                                onRecurringGroupMoved={handleRecurringGroupMoved}
                                onRecurringItemMoved={handleRecurringItemMoved}
                                onAddRecurringGroup={handleAddRecurringGroup}
                                onDeleteRecurringGroup={handleDeleteRecurringGroup}
                                onUpfrontGroupExpandedChanged={handleUpfrontGroupExpandedChanged}
                                onRecurringGroupExpandedChanged={handleRecurringGroupExpandedChanged}
                                onEditRecurringGroup={handleEditRecurringGroup}
                                onEditUpfrontGroup={handleEditUpfrontGroup}
                                onUpdateRecurringVisibleGroupOrItem={handleUpdateRecurringVisibleGroupOrItem}
                                onUpdateUpfrontVisibleGroupOrItem={handleUpdateUpfrontVisibleGroupOrItem}
                                onUpdateQuoteTemplateId={(newId) => updateQuoteRevision("quoteTemplateId", newId)}
                                onUpdateQuoteAdditionalFields={(newFields) => updateQuoteRevision("quoteAdditionalFields", newFields)}
                                onUpdateUpfrontShowChildren={handleUpdateUpfrontShowChildren}
                                onUpdateRecurringShowChildren={handleUpdateRecurringShowChildren}
                            />
                        )}
                    </>
                )
            }
            {
                currentActiveMainTab === "Exports" && !isLoadingQuoteItems && (
                    <QuoteExports
                        quoteRevision={quoteRevision}
                        quoteItems={allEnabledQuoteItemsWithCalculatedProperties}
                        onUpdateQuoteAdditionalFields={(newFields) => updateQuoteRevision("quoteAdditionalFields", newFields)}
                    />
                )
            }
            {
                currentActiveMainTab === "Review" && !isLoadingQuoteItems && (
                    <QuoteReview
                        quoteRevision={quoteRevision}
                        quoteCustomisation={{
                            ...(quoteRevision.quoteCustomisation ?? {}),
                            groups: {
                                upfront: Array.isArray(quoteRevision?.quoteCustomisation?.groups?.upfront)
                                    ? quoteRevision.quoteCustomisation.groups.upfront
                                    : [],
                                recurring: Array.isArray(quoteRevision?.quoteCustomisation?.groups?.recurring)
                                    ? quoteRevision.quoteCustomisation.groups.recurring
                                    : [],
                            },
                        }}
                        quoteItems={allEnabledQuoteItemsWithCalculatedProperties}
                        quoteModules={quoteModules}
                        onAddWarning={handleAddWarning}
                        onRemoveWarning={handleRemoveWarning}
                        onAcknowledgedWarningChange={handleAcknowledgedWarningChange}
                        onApprovedWarningChange={handleApprovedWarningChange}
                        lockRevision={lockRevision}
                        isBasketProcessingActive={isBasketProcessingActive}
                        isActive={quoteDataRef.current?.isActive && quoteRevisionRef.current?.isMain ? true : false}
                    />
                )
            }
            {
                currentActiveMainTab === "Revisions" && (
                    <QuoteRevisions
                        quoteId={quoteId}
                        quoteRevisionId={quoteRevisionId}
                        quoteRevisions={quoteRevisions}
                        isLoadingQuoteRevisions={isLoadingQuoteRevisions}
                        quoteRevisionsError={quoteRevisionsError}
                        refetchQuoteRevisions={refetchQuoteRevisions}
                        setQuoteRevisionsFilter={setQuoteRevisionsFilter}
                        quoteRevisionsTotalRecords={quoteRevisionsTotalRecords}
                        isActive={quoteData?.isActive}
                        quoteData={quoteData}
                        sharepointFolder={opportunitySharepointFolder}
                    />
                )
            }
            {
                currentActiveMainTab === "Additional Information" &&
                    <QuoteRevisionNotes onRevisionNotesChanged={handleRevisionNotesChanged} />
            }
            {
                currentActiveMainTab === "History" && (
                    <Container fluid>
                        <DataTablePageQuery
                            className="mb-5 pb-5"
                            columns={quoteHistoryColumns}
                            data={quoteHistoryData ?? []}
                            queryForDownload={getQuoteHistoryQueryForDownload}
                            downloadButtonIsEnabled={quoteHistoryDownloadButtonIsEnabled}
                            csvFileName="quote_history.csv"
                            isLoading={isLoadingQuoteHistory || isLoadingUsersLookup}
                            error={quoteHistoryError || usersLookupError}
                            totalRecords={quoteHistoryTotalRecords}
                            onFilterChange={changeQuoteHistoryFilter}
                            sortConfiguration={{ key: "timestamp", direction: "descending" }}
                        />

                        <br />
                        <br />
                    </Container>
                )
            }
            <QuoteModal
                modalOpen={quoteModalOpen}
                setModalOpen={setQuoteModalOpen}
                opportunity={opportunityData}
                quote={quoteData}
            />
            </div>
            
            {/* Sticky Footer */}
            {showPageFooter && (
                <footer style={{ 
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: 'var(--bg-color-contrast1)', 
                    borderTop: '1px solid var(--bs-border-color)',
                    padding: '1rem 0'
                }}>
                    <Row className="text-muted">
                            <Col>
                                {basketProcessingState !== "Basket Processing Idle" && (
                                    <Row>
                                        <Col className="text-center">
                                            <span style={{visibility: !(basketProcessingState.includes("locked") || basketProcessingState.includes("complete") || basketProcessingState.includes("finalised") || basketProcessingState.includes("Basket Has Changed") ) ? "visible" : "hidden"}}>
                                                <Spinner color="primary" size="sm" />
                                            </span>
                                            <span  className="ms-3">
                                                {basketProcessingState}
                                            </span>
                                        </Col>
                                    </Row>
                                )}
                                {basketProcessingState !== "Basket Processing Idle" && showSalesforceSyncFooter && (
                                    <hr className="my-2" />
                                )}
                                {showSalesforceSyncFooter && (
                                    <Row>
                                        <Col className="text-center">
                                            {["Idle", "Error", "Warning", "Failed"].includes(salesforceSync.salesforceSyncState) && salesforceSync.lastRun?.executedBy === salesforceSyncExecutionById && (   
                                                <span className="ms-3">                                                
                                                    {`Salesforce Sync ${salesforceSync.lastRun?.result} ${salesforceSync.lastRun?.detail ? ` - ${salesforceSync.lastRun.detail}` : ""}`}
                                                </span>
                                            )} 
                                            {!["Idle", "Initialising", "Error", "Warning", "Failed"].includes(salesforceSync.salesforceSyncState) && salesforceSync.executingBy === salesforceSyncExecutionById && (
                                                <>
                                                    <span style={{visibility: !["Idle", "Initialising", "Error", "Warning", "Failed"].includes(salesforceSync.salesforceSyncState) ? "visible" : "hidden"}}>
                                                        <Spinner color="primary" size="sm" />
                                                    </span>

                                                    <span className="ms-3">                                                
                                                        {`Salesforce Sync ${salesforceSync.salesforceSyncState}${salesforceSync.salesforceSyncMessage ? ` - ${salesforceSync.salesforceSyncMessage}` : ""}`}
                                                    </span>
                                                </>
                                            )}
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                </footer>
            )}
        </div>
    );
}
export default ViewQuote;

